import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { projectsAPI, aiAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import AIUsageDisplay from '../components/AIUsageDisplay';
import TutorialOverlay, { useTutorial, HelpMenuButton } from '../components/TutorialOverlay';
import TrialUpgradeModal from '../components/TrialUpgradeModal';
import { 
  ArrowLeft, Upload, MousePointer, Circle as CircleIcon, 
  ZoomIn, ZoomOut, Maximize, Download, FileSpreadsheet, FileText,
  Sparkles, Trash2, Loader2, Check, X, Image, Plus, Settings2, Settings,
  ChevronRight, ChevronDown, HelpCircle, Info, Pencil, RotateCw, Save, Copy, ClipboardPaste
} from 'lucide-react';
import { Progress } from '../components/ui/progress';

// SmartBalloon Tutorial Steps
// Tutorial steps factory - returns steps with translated strings
const getSmartBalloonTutorialSteps = (t) => [
  {
    targetSelector: '[data-testid="upload-area"]',
    title: t('tutorial_upload_title'),
    description: t('tutorial_upload_desc'),
    icon: <Upload className="w-5 h-5 text-blue-600" />
  },
  {
    targetSelector: '[data-testid="ai-detect-btn"], [data-testid="add-marker-tool"]',
    title: t('tutorial_add_balloon_title'),
    description: t('tutorial_add_balloon_desc'),
    icon: <Sparkles className="w-5 h-5 text-blue-600" />
  },
  {
    targetSelector: '[data-testid="marker-properties-panel"]',
    title: t('tutorial_edit_props_title'),
    description: t('tutorial_edit_props_desc'),
    icon: <Settings2 className="w-5 h-5 text-blue-600" />
  },
  {
    targetSelector: '[data-testid="export-pdf-btn"], [data-testid="export-excel-btn"]',
    title: t('tutorial_export_title'),
    description: t('tutorial_export_desc'),
    icon: <Download className="w-5 h-5 text-blue-600" />
  }
];

// Separate component for editing suggestions - uses local string state for inputs
const EditSuggestionForm = ({ suggestion, markers = [], onSave, onAccept, onCancel }) => {
  // Local state as STRINGS to allow typing intermediate values like "-" or "0."
  const [description, setDescription] = useState(suggestion.description || '');
  const [nominal, setNominal] = useState(suggestion.nominal?.toString() ?? '');
  const [tolPlus, setTolPlus] = useState(suggestion.tol_plus?.toString() ?? '');
  const [tolMinus, setTolMinus] = useState(suggestion.tol_minus?.toString() ?? '');
  const [markerNumber, setMarkerNumber] = useState(suggestion.marker_number?.toString() ?? '');
  const [instrument, setInstrument] = useState(suggestion.instrument || '');
  const [resolution, setResolution] = useState(suggestion.resolution || '');
  const [showInstrumentSuggestions, setShowInstrumentSuggestions] = useState(false);

  // Get unique instruments with their resolutions from all markers
  const instrumentMap = useMemo(() => {
    const map = new Map();
    markers.forEach(m => {
      if (m.instrument && m.instrument.trim()) {
        const key = m.instrument.toLowerCase().trim();
        if (!map.has(key)) {
          map.set(key, { 
            instrument: m.instrument.trim(), 
            resolution: m.resolution || '' 
          });
        }
      }
    });
    return map;
  }, [markers]);
  
  // Filter suggestions based on input
  const instrumentSuggestions = useMemo(() => {
    if (!instrument || instrument.length < 2) return [];
    const search = instrument.toLowerCase();
    return Array.from(instrumentMap.values())
      .filter(item => item.instrument.toLowerCase().includes(search))
      .slice(0, 5);
  }, [instrument, instrumentMap]);

  // Convert string values to numbers for saving
  const getUpdatedSuggestion = () => {
    const parseNum = (str) => {
      if (!str || str === '' || str === '-' || str === '+' || str === '.') return null;
      const cleaned = str.replace(',', '.');
      const num = parseFloat(cleaned);
      return isNaN(num) ? null : num;
    };
    
    const parseIntNum = (str) => {
      if (!str || str === '') return null;
      const num = parseInt(str, 10);
      return isNaN(num) || num <= 0 ? null : num;
    };
    
    return {
      ...suggestion,
      description: description,
      nominal: parseNum(nominal),
      tol_plus: parseNum(tolPlus),
      tol_minus: parseNum(tolMinus),
      marker_number: parseIntNum(markerNumber),
      instrument: instrument || null,
      resolution: resolution || null
    };
  };

  const handleSelectInstrument = (item) => {
    setInstrument(item.instrument);
    if (item.resolution) {
      setResolution(item.resolution);
    }
    setShowInstrumentSuggestions(false);
  };

  return (
    <>
      <div className="space-y-4 py-4">
        {/* Numero Marker */}
        <div>
          <Label className="text-sm font-medium">Numero Marker (opzionale)</Label>
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={markerNumber}
            onChange={(e) => setMarkerNumber(e.target.value.replace(/[^0-9]/g, ''))}
            className="mt-1 w-24"
            placeholder="es. 1, 2, 3..."
          />
          <p className="text-xs text-slate-400 mt-1">Lascia vuoto per numerazione automatica</p>
        </div>
        
        {/* Descrizione Quota */}
        <div>
          <Label className="text-sm font-medium">Descrizione Quota</Label>
          <Input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1"
            placeholder="es. Ø47 +0,1/0"
          />
        </div>
        
        {/* Quota Nominale */}
        <div>
          <Label className="text-sm font-medium">Quota Nominale</Label>
          <Input
            type="text"
            inputMode="decimal"
            value={nominal}
            onChange={(e) => setNominal(e.target.value.replace(',', '.'))}
            className="mt-1"
            placeholder="es. 21.5, -5, 0.3"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium">Tolleranza Superiore</Label>
            <Input
              type="text"
              inputMode="decimal"
              value={tolPlus}
              onChange={(e) => setTolPlus(e.target.value.replace(',', '.'))}
              className="mt-1"
              placeholder="es. 0.1, -0.05, 0"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Tolleranza Inferiore</Label>
            <Input
              type="text"
              inputMode="decimal"
              value={tolMinus}
              onChange={(e) => setTolMinus(e.target.value.replace(',', '.'))}
              className="mt-1"
              placeholder="es. -0.1, 0, -0.05"
            />
          </div>
        </div>

        {/* Strumento e Risoluzione */}
        <div className="grid grid-cols-2 gap-4">
          <div className="relative">
            <Label className="text-sm font-medium">Strumento di Misura</Label>
            <Input
              type="text"
              value={instrument}
              onChange={(e) => {
                setInstrument(e.target.value);
                setShowInstrumentSuggestions(true);
              }}
              onFocus={() => setShowInstrumentSuggestions(true)}
              onBlur={() => setTimeout(() => setShowInstrumentSuggestions(false), 200)}
              className="mt-1"
              placeholder="es. calibro, micrometro"
            />
            {showInstrumentSuggestions && instrumentSuggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-auto">
                {instrumentSuggestions.map((item, idx) => (
                  <div
                    key={idx}
                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer flex justify-between items-center text-sm"
                    onMouseDown={() => handleSelectInstrument(item)}
                  >
                    <span className="font-medium">{item.instrument}</span>
                    {item.resolution && (
                      <span className="text-slate-400 text-xs">ris: {item.resolution}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="text-sm font-medium">Risoluzione</Label>
            <Input
              type="text"
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              className="mt-1"
              placeholder="es. 0.01, 0.001"
            />
          </div>
        </div>
        
        <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
          <strong>Nota:</strong> Inserisci i valori numerici. Es: 0.1 (positivo), -0.1 (negativo), 0 (zero).
          <br/>Testo OCR originale: <span className="font-mono">{suggestion.description}</span>
          {instrumentMap.size > 0 && (
            <>
              <br/><strong>Suggerimento:</strong> Digita nel campo strumento per vedere gli strumenti già usati nel progetto.
            </>
          )}
        </div>
      </div>
      
      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={onCancel}>
          Annulla
        </Button>
        <Button onClick={() => onSave(getUpdatedSuggestion())}>
          Salva
        </Button>
        <Button 
          variant="default"
          className="bg-green-600 hover:bg-green-700"
          onClick={() => onAccept(getUpdatedSuggestion())}
        >
          Salva e Accetta
        </Button>
      </DialogFooter>
    </>
  );
};

// Controlled input for tol_minus with local state to avoid re-render issues
const TolMinusInput = ({ marker, updateMarker }) => {
  const [localValue, setLocalValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  
  // Sync from marker when not focused
  useEffect(() => {
    if (!isFocused) {
      const val = marker?.tol_minus_str ?? marker?.tol_minus ?? '';
      setLocalValue(val === null ? '' : String(val));
    }
  }, [marker?.tol_minus, marker?.tol_minus_str, isFocused]);
  
  return (
    <Input
      type="text"
      inputMode="decimal"
      value={localValue}
      onFocus={() => setIsFocused(true)}
      onChange={(e) => {
        const val = e.target.value.replace(',', '.');
        setLocalValue(val);
      }}
      onBlur={(e) => {
        setIsFocused(false);
        const val = e.target.value.replace(',', '.');
        const parsed = parseFloat(val);
        if (val === '' || isNaN(parsed)) {
          updateMarker('tol_minus', null);
          updateMarker('tol_minus_str', '');
          setLocalValue('');
        } else {
          updateMarker('tol_minus', parsed);
          updateMarker('tol_minus_str', parsed.toString());
          setLocalValue(parsed.toString());
        }
      }}
      className="dense-input font-mono"
      data-testid="marker-tol-minus"
      placeholder="es. -0.1"
    />
  );
};

// Controlled input for tol_plus with local state to avoid re-render issues
const TolPlusInput = ({ marker, updateMarker }) => {
  const [localValue, setLocalValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  
  // Sync from marker when not focused
  useEffect(() => {
    if (!isFocused) {
      const val = marker?.tol_plus_str ?? marker?.tol_plus ?? '';
      setLocalValue(val === null ? '' : String(val));
    }
  }, [marker?.tol_plus, marker?.tol_plus_str, isFocused]);
  
  return (
    <Input
      type="text"
      inputMode="decimal"
      value={localValue}
      onFocus={() => setIsFocused(true)}
      onChange={(e) => {
        const val = e.target.value.replace(',', '.');
        setLocalValue(val);
      }}
      onBlur={(e) => {
        setIsFocused(false);
        const val = e.target.value.replace(',', '.');
        const parsed = parseFloat(val);
        if (val === '' || isNaN(parsed)) {
          updateMarker('tol_plus', null);
          updateMarker('tol_plus_str', '');
          setLocalValue('');
        } else {
          updateMarker('tol_plus', parsed);
          updateMarker('tol_plus_str', parsed.toString());
          setLocalValue(parsed.toString());
        }
      }}
      className="dense-input font-mono"
      data-testid="marker-tol-plus"
      placeholder="es. +0.1"
    />
  );
};

// Controlled input for nominal with local state to allow typing decimals
const NominalInput = ({ marker, updateMarker, t }) => {
  const [localValue, setLocalValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  
  // Sync from marker when not focused
  useEffect(() => {
    if (!isFocused) {
      const val = marker?.nominal ?? '';
      setLocalValue(val === null ? '' : String(val));
    }
  }, [marker?.nominal, isFocused]);
  
  return (
    <Input
      type="text"
      inputMode="decimal"
      value={localValue}
      onFocus={() => setIsFocused(true)}
      onChange={(e) => {
        // Allow comma as decimal separator, convert to dot
        const val = e.target.value.replace(',', '.');
        setLocalValue(val);
      }}
      onBlur={(e) => {
        setIsFocused(false);
        const val = e.target.value.replace(',', '.');
        const parsed = parseFloat(val);
        if (val === '' || isNaN(parsed)) {
          updateMarker('nominal', null);
          setLocalValue('');
        } else {
          updateMarker('nominal', parsed);
          setLocalValue(parsed.toString());
        }
      }}
      className="dense-input font-mono"
      data-testid="marker-nominal"
      placeholder="es. 21.5"
    />
  );
};

// Controlled input for marker number - validates only on blur/Enter
const MarkerNumberInput = ({ marker, updateMarker }) => {
  const [localValue, setLocalValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  
  // Sync from marker when not focused
  useEffect(() => {
    if (!isFocused) {
      const val = marker?.number ?? '';
      setLocalValue(val === null ? '' : String(val));
    }
  }, [marker?.number, isFocused]);
  
  const handleConfirm = () => {
    setIsFocused(false);
    const val = parseInt(localValue, 10);
    if (localValue === '' || isNaN(val) || val <= 0) {
      // Reset to original value
      setLocalValue(String(marker?.number || 1));
    } else if (val !== marker?.number) {
      // Only update if number changed
      updateMarker('number', val);
    }
  };
  
  return (
    <Input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={localValue}
      onFocus={() => setIsFocused(true)}
      onChange={(e) => {
        // Only allow digits
        const val = e.target.value.replace(/[^0-9]/g, '');
        setLocalValue(val);
      }}
      onBlur={handleConfirm}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.target.blur(); // Trigger blur to confirm
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setLocalValue(String(marker?.number || 1));
          e.target.blur();
        }
      }}
      className="w-16 h-7 text-center font-bold font-mono"
      data-testid="marker-number-input"
    />
  );
};

// Autocomplete input for instrument with suggestions from previously used values
const InstrumentAutocomplete = ({ marker, markers, updateMarker }) => {
  const [localValue, setLocalValue] = useState(marker?.instrument || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  
  // Sync from marker when not focused
  useEffect(() => {
    if (!isFocused) {
      setLocalValue(marker?.instrument || '');
    }
  }, [marker?.instrument, isFocused]);
  
  // Get unique instruments with their resolutions from all markers
  const instrumentMap = useMemo(() => {
    const map = new Map();
    markers.forEach(m => {
      if (m.instrument && m.instrument.trim()) {
        const key = m.instrument.toLowerCase().trim();
        if (!map.has(key)) {
          map.set(key, { 
            instrument: m.instrument.trim(), 
            resolution: m.resolution || '' 
          });
        }
      }
    });
    return map;
  }, [markers]);
  
  // Filter suggestions based on input
  const suggestions = useMemo(() => {
    if (!localValue || localValue.length < 2) return [];
    const search = localValue.toLowerCase();
    return Array.from(instrumentMap.values())
      .filter(item => item.instrument.toLowerCase().includes(search))
      .slice(0, 5);
  }, [localValue, instrumentMap]);
  
  const handleSelect = (item) => {
    setLocalValue(item.instrument);
    updateMarker('instrument', item.instrument);
    // Auto-fill resolution
    if (item.resolution) {
      updateMarker('resolution', item.resolution);
    }
    setShowSuggestions(false);
  };
  
  return (
    <div className="relative">
      <Input
        ref={inputRef}
        type="text"
        value={localValue}
        onFocus={() => {
          setIsFocused(true);
          setShowSuggestions(true);
        }}
        onChange={(e) => {
          setLocalValue(e.target.value);
          setShowSuggestions(true);
        }}
        onBlur={() => {
          // Delay to allow click on suggestion
          setTimeout(() => {
            setIsFocused(false);
            setShowSuggestions(false);
            updateMarker('instrument', localValue);
          }, 200);
        }}
        className="dense-input"
        data-testid="marker-instrument"
        placeholder="es. calibro, micrometro"
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-auto">
          {suggestions.map((item, idx) => (
            <div
              key={idx}
              className="px-3 py-2 hover:bg-blue-50 cursor-pointer flex justify-between items-center text-sm"
              onMouseDown={() => handleSelect(item)}
            >
              <span className="font-medium">{item.instrument}</span>
              {item.resolution && (
                <span className="text-slate-400 text-xs">ris: {item.resolution}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const WorkspacePage = () => {
  const { t, i18n } = useTranslation();
  const { projectId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  // Tutorial system
  const { shouldShow: shouldShowTutorial, resetTutorial } = useTutorial('smartballoon_intro', token);
  const [showTutorial, setShowTutorial] = useState(false);
  
  // State
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tool, setTool] = useState('select'); // select, add, manual, paste
  const [copiedMarker, setCopiedMarker] = useState(null);
  const [isPasteMode, setIsPasteMode] = useState(false); // Visual indicator for paste mode
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);  // Track unsaved marker changes
  const [hasUnsavedSuggestions, setHasUnsavedSuggestions] = useState(false);  // Track unsaved AI suggestions
  const [savingMarkers, setSavingMarkers] = useState(false);  // Saving state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [aiDetecting, setAiDetecting] = useState(false);
  const [aiProgress, setAiProgress] = useState({ progress: 0, total: 100, found: 0 });  // AI progress bar
  const [aiSuggestions, setAiSuggestionsRaw] = useState([]);
  
  // Wrapper to track unsaved AI suggestions
  const setAiSuggestions = useCallback((valueOrUpdater) => {
    setAiSuggestionsRaw(prev => {
      const newValue = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : valueOrUpdater;
      // Mark as unsaved if we're changing from empty to non-empty, or vice versa, or modifying
      if (newValue.length > 0 || prev.length > 0) {
        setHasUnsavedSuggestions(true);
      }
      return newValue;
    });
  }, []);
  
  const [editingSuggestion, setEditingSuggestion] = useState(null);  // For modify modal
  const [exporting, setExporting] = useState(false);
  const [readingDimension, setReadingDimension] = useState(false);
  const [generalTolerances, setGeneralTolerances] = useState(null);
  const [analyzingTolerances, setAnalyzingTolerances] = useState(false);
  const [showISIR, setShowISIR] = useState(false);  // ISIR panel collapsed by default
  const [showInstructions, setShowInstructions] = useState(false);  // Instructions popup
  const [aiUsage, setAiUsage] = useState(null);  // AI usage tracking
  const [highlightedMarkerId, setHighlightedMarkerId] = useState(null);  // Highlighted marker for zoom
  const [activeTab, setActiveTab] = useState('properties');  // Controlled tab state
  const [showManualMarkerDialog, setShowManualMarkerDialog] = useState(false);  // Manual marker input
  const [manualMarkerData, setManualMarkerData] = useState({ nominal: '', tol_plus: '', tol_minus: '', description: '', instrument: '', resolution: '' });
  const [pendingManualPosition, setPendingManualPosition] = useState(null);  // Position for manual marker
  const [manualDialogPos, setManualDialogPos] = useState({ x: 0, y: 0 });  // Draggable position
  const [isDraggingManualDialog, setIsDraggingManualDialog] = useState(false);  // Dragging state
  const [undoStack, setUndoStack] = useState([]);  // For UNDO functionality
  const [hoveredSuggestion, setHoveredSuggestion] = useState(null);  // Hovered AI suggestion for tooltip
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);  // Selected AI suggestion (for DELETE)
  const [draggingSuggestion, setDraggingSuggestion] = useState(null);  // AI suggestion being dragged
  const [imageRotation, setImageRotation] = useState(0);  // Image rotation (0, 90, 180, 270)
  
  // Marker number change confirmation dialog
  const [pendingNumberChange, setPendingNumberChange] = useState(null);  // { markerId, newNumber, existingNumber }
  const [pendingAISuggestionAccept, setPendingAISuggestionAccept] = useState(null);  // { suggestion, newNumber }
  const [showRescanConfirm, setShowRescanConfirm] = useState(false);  // Confirm before re-scanning
  
  // NEW: View-by-View AI Detection State
  const [detectedViews, setDetectedViews] = useState([]);  // Detected views from Phase 1
  const [detectingViews, setDetectingViews] = useState(false);  // Loading state for view detection
  const [detectingMultiPass, setDetectingMultiPass] = useState(false);  // Loading state for Multi-Pass detection
  const [scanningView, setScanningView] = useState(null);  // Currently scanning view index
  const [scannedViews, setScannedViews] = useState([]);  // List of already scanned view indices
  const [showAIConfirmDialog, setShowAIConfirmDialog] = useState(false);  // Confirmation before AI Bulk
  const [hoveredView, setHoveredView] = useState(null);  // Hovered view for highlight
  
  // NEW: Manual View Definition State
  const [manualViews, setManualViews] = useState([]);  // User-defined view rectangles
  const [editingManualView, setEditingManualView] = useState(null);  // Currently being resized/moved
  const [resizeHandle, setResizeHandle] = useState(null);  // Which handle is being dragged
  const [dragStartPos, setDragStartPos] = useState(null);  // Starting position for drag
  const [scanningManualView, setScanningManualView] = useState(null);  // Index of manual view being scanned
  const [scanRotated, setScanRotated] = useState(true);  // Also scan rotated 90° for vertical text
  const wasEditingRef = useRef(false);  // Track if we just finished editing (to prevent click)
  
  // NEW: Tiled Drawing State (for large PDFs like "lenzuolo")
  const [isTiled, setIsTiled] = useState(false);  // Whether the drawing is tiled
  const [tiles, setTiles] = useState([]);  // Tile metadata
  const [activeTileIndex, setActiveTileIndex] = useState(null);  // Currently displayed tile (null = overview)
  const [activeTileImage, setActiveTileImage] = useState(null);  // Loaded tile image data
  const [loadingTile, setLoadingTile] = useState(false);  // Loading state for tile
  const [editingTileName, setEditingTileName] = useState(null);  // Tile being renamed
  const [editingViewName, setEditingViewName] = useState(null);  // Manual view being renamed
  
  // CMM Report State
  const [parsingCMMReport, setParsingCMMReport] = useState(false);
  const [cmmMeasurements, setCmmMeasurements] = useState([]);  // Extracted measurements for preview
  const [cmmStats, setCmmStats] = useState(null);  // Stats from parsing
  const [showCMMPreview, setShowCMMPreview] = useState(false);  // Show preview dialog
  const [selectedCMMRows, setSelectedCMMRows] = useState(new Set());  // Selected rows to import
  const [applyingCMM, setApplyingCMM] = useState(false);  // Applying in progress
  const [cmmReportStatus, setCmmReportStatus] = useState(null);  // {has_report, filename} - legacy single
  
  // CMM Multiple Reports State (NEW)
  const [cmmReports, setCmmReports] = useState([]);  // List of uploaded reports
  const [uploadingCMMReports, setUploadingCMMReports] = useState(false);
  const [cmmDuplicates, setCmmDuplicates] = useState([]);  // Duplicate SB IDs requiring resolution
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [currentDuplicateIndex, setCurrentDuplicateIndex] = useState(0);
  const [resolvedDuplicates, setResolvedDuplicates] = useState({});  // sb_id -> selected measurement
  
  // CMM Multi-Sample Mode State (NEW)
  const [showCMMImportModeDialog, setShowCMMImportModeDialog] = useState(false);
  const [cmmImportMode, setCmmImportMode] = useState('multi_sample');  // 'duplicate' or 'multi_sample' or 'manual_assign'
  
  // CMM Manual Assignment State
  const [showCMMManualAssignDialog, setShowCMMManualAssignDialog] = useState(false);
  const [cmmReportAssignments, setCmmReportAssignments] = useState([]);  // [{report_id, filename, sample_name}]
  
  // Upload state
  const [uploadingDrawing, setUploadingDrawing] = useState(false);
  const [showCMMHelp, setShowCMMHelp] = useState(false);  // CMM format instructions
  
  // Trial export modal state
  const [trialExportModalOpen, setTrialExportModalOpen] = useState(false);
  const isTrial = user?.plan?.toUpperCase() === 'AI_TRIAL';
  
  // Dialog drag state for "Modifica Quota" popup
  const [editDialogPos, setEditDialogPos] = useState({ x: 150, y: 0 });  // Start offset right to avoid covering drawing
  const [isDraggingDialog, setIsDraggingDialog] = useState(false);
  const dialogDragStart = useRef({ x: 0, y: 0, startX: 0, startY: 0 });
  
  // EXPERIMENTAL: Vector PDF Detection State
  const [vectorInfo, setVectorInfo] = useState(null);  // { feature_enabled, is_vector_pdf }
  const [detectingVector, setDetectingVector] = useState(false);
  
  // Tolerance Class Selection
  const [showToleranceModal, setShowToleranceModal] = useState(false);
  const [selectedToleranceClass, setSelectedToleranceClass] = useState('m');
  const [manualTolerance, setManualTolerance] = useState('0.1');
  
  // Real-time dimension preview - DISABLED (unreliable OCR)
  // const [previewDimension, setPreviewDimension] = useState(null);
  // const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  // const previewTimeoutRef = useRef(null);
  // const lastPreviewPos = useRef({ x: 0, y: 0 });
  
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  // Show tutorial when workspace opens (before drawing upload)
  useEffect(() => {
    if (shouldShowTutorial && !loading && project) {
      const timer = setTimeout(() => setShowTutorial(true), 500);
      return () => clearTimeout(timer);
    }
  }, [shouldShowTutorial, loading, project]);

  // Load project
  useEffect(() => {
    loadProject();
    loadAiUsage();
    loadVectorInfo();  // EXPERIMENTAL: Load vector PDF info
  }, [projectId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle if user is typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      // Delete selected marker with Delete or Backspace key
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedMarker) {
        e.preventDefault();
        
        // Save state for undo
        setUndoStack(prev => [...prev, [...markers]]);
        
        // Inline delete logic - KEEP original marker numbers
        const newMarkers = markers.filter(m => m.id !== selectedMarker.id);
        
        setMarkers(newMarkers);
        setSelectedMarker(null);
        
        // Save to backend
        projectsAPI.updateMarkers(projectId, newMarkers).catch(err => {
          console.error('Failed to save markers:', err);
        });
        return;
      }
      
      // Delete selected AI suggestion with Delete or Backspace key
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedSuggestion) {
        e.preventDefault();
        setAiSuggestions(prev => prev.filter(s => s.id !== selectedSuggestion.id));
        setSelectedSuggestion(null);
        toast.success('Suggerimento AI rimosso');
        return;
      }
      
      // Escape to deselect marker or suggestion
      if (e.key === 'Escape') {
        setSelectedMarker(null);
        setSelectedSuggestion(null);
      }
    };
    
    // Auto fit-to-screen on project load
    const handleFitToScreenEvent = () => {
      handleFitToScreen();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('fitToScreen', handleFitToScreenEvent);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('fitToScreen', handleFitToScreenEvent);
    };
  }, [selectedMarker, selectedSuggestion, markers, projectId, aiSuggestions]); // Dependencies for the handler

  const loadAiUsage = async () => {
    try {
      const response = await aiAPI.getUsage();
      setAiUsage(response.data);
    } catch (error) {
      // Silently fail - AI usage is not critical
      console.log('Could not load AI usage:', error);
    }
  };

  const loadProject = async () => {
    try {
      const response = await projectsAPI.get(projectId);
      setProject(response.data);
      setMarkers(response.data.markers || []);
      if (response.data.general_tolerances) {
        setGeneralTolerances(response.data.general_tolerances);
      }
      
      // Load saved AI suggestions if any (use Raw setter to avoid marking as unsaved)
      if (response.data.ai_suggestions && response.data.ai_suggestions.length > 0) {
        setAiSuggestionsRaw(response.data.ai_suggestions);
        console.log('[AI] Loaded', response.data.ai_suggestions.length, 'saved AI suggestions');
      }
      
      // Check if drawing is tiled (large PDF)
      if (response.data.is_tiled && response.data.drawing_tiles) {
        try {
          const tilesData = typeof response.data.drawing_tiles === 'string' 
            ? JSON.parse(response.data.drawing_tiles) 
            : response.data.drawing_tiles;
          setTiles(tilesData);
          setIsTiled(true);
          setActiveTileIndex(null);  // Start with overview
          setActiveTab('tiles');  // Open tiles tab by default for tiled drawings
          console.log('[Tiles] Loaded tiled drawing with', tilesData.length, 'tiles');
        } catch (e) {
          console.error('[Tiles] Error parsing tiles:', e);
          setIsTiled(false);
        }
      } else {
        setIsTiled(false);
        setTiles([]);
      }
      
      // Check CMM report status (legacy single)
      try {
        const cmmResponse = await projectsAPI.getMeasurementReportStatus(projectId);
        setCmmReportStatus(cmmResponse.data);
      } catch (e) {
        setCmmReportStatus(null);
      }
      
      // Load CMM reports list (multiple)
      try {
        const reportsResponse = await projectsAPI.getCMMReportsList(projectId);
        setCmmReports(reportsResponse.data.reports || []);
      } catch (e) {
        setCmmReports([]);
      }
      
      // Trigger fit-to-screen after image loads
      if (response.data.drawing_base64) {
        setTimeout(() => {
          // Call fit to screen after a short delay to ensure image is rendered
          const event = new CustomEvent('fitToScreen');
          window.dispatchEvent(event);
        }, 500);
      }
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Load a specific tile image for large drawings
  const loadTileImage = async (tileIndex) => {
    if (!isTiled || !tileIndex) {
      setActiveTileIndex(null);
      setActiveTileImage(null);
      return;
    }
    
    setLoadingTile(true);
    try {
      const response = await projectsAPI.getTileImage(projectId, tileIndex);
      if (response.data?.tile_data) {
        setActiveTileImage(response.data.tile_data);
        setActiveTileIndex(tileIndex);
        toast.success(`Caricata ${response.data.tile_name || `Parte ${tileIndex}`}`);
        
        // Auto-zoom to fit the tile (centra tutto)
        // Small delay to ensure image is rendered
        setTimeout(() => {
          handleFitToScreen();
        }, 150);
      }
    } catch (error) {
      console.error('[Tiles] Error loading tile:', error);
      toast.error('Errore nel caricamento della sezione');
    } finally {
      setLoadingTile(false);
    }
  };

  // Rename a tile
  const handleRenameTile = async (tileIndex, newName) => {
    try {
      await projectsAPI.renameTile(projectId, tileIndex, newName);
      // Update local state
      setTiles(prev => prev.map(t => 
        t.index === tileIndex ? { ...t, name: newName } : t
      ));
      setEditingTileName(null);
      toast.success('Nome aggiornato');
    } catch (error) {
      console.error('[Tiles] Error renaming tile:', error);
      toast.error('Errore nel rinominare');
    }
  };

  // Switch to overview (for tiled drawings)
  const showOverview = () => {
    setActiveTileIndex(null);
    setActiveTileImage(null);
    // Reset zoom when returning to overview
    setTimeout(() => {
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }, 100);
  };

  // Rename a manual view
  const handleRenameView = (viewId, newName) => {
    if (!newName.trim()) return;
    setManualViews(prev => prev.map(v => 
      v.id === viewId ? { ...v, name: newName.trim() } : v
    ));
    setEditingViewName(null);
    toast.success('Vista rinominata');
  };

  // ============== CMM REPORT FUNCTIONS ==============
  
  // Upload CMM Report
  const onDropCMMReport = useCallback(async (files) => {
    if (files.length === 0) return;
    const file = files[0];
    
    if (file.type !== 'application/pdf') {
      toast.error('Solo file PDF accettati');
      return;
    }
    
    try {
      const response = await projectsAPI.uploadMeasurementReport(projectId, file);
      setCmmReportStatus({ has_report: true, filename: response.data.filename });
      toast.success('Report CMM caricato');
    } catch (error) {
      toast.error('Upload fallito: ' + (error.response?.data?.detail || 'Errore'));
    }
  }, [projectId]);

  const { getRootProps: getCMMRootProps, getInputProps: getCMMInputProps } = useDropzone({
    onDrop: onDropCMMReport,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1
  });

  // Delete CMM Report
  const handleDeleteCMMReport = async () => {
    try {
      await projectsAPI.deleteMeasurementReport(projectId);
      setCmmReportStatus(null);
      toast.success('Report CMM rimosso');
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  // Parse CMM Report and show preview
  const handleParseCMMReport = async () => {
    setParsingCMMReport(true);
    try {
      const response = await projectsAPI.parseCMMReport(projectId);
      const { measurements, stats } = response.data;
      
      if (measurements.length === 0) {
        toast.warning('Nessun identificatore SB trovato nel report');
        return;
      }
      
      setCmmMeasurements(measurements);
      setCmmStats(stats);
      // Select all matched markers by default
      setSelectedCMMRows(new Set(
        measurements.filter(m => m.marker_exists).map(m => m.sb_id)
      ));
      setShowCMMPreview(true);
      
      toast.success(`Trovati ${measurements.length} valori SB`);
    } catch (error) {
      toast.error('Errore nel parsing: ' + (error.response?.data?.detail || 'Errore'));
    } finally {
      setParsingCMMReport(false);
    }
  };

  // Toggle selection of a CMM row
  const toggleCMMRow = (sbId) => {
    setSelectedCMMRows(prev => {
      const next = new Set(prev);
      if (next.has(sbId)) {
        next.delete(sbId);
      } else {
        next.add(sbId);
      }
      return next;
    });
  };

  // Select/deselect all CMM rows
  const toggleAllCMMRows = () => {
    const matchedIds = cmmMeasurements.filter(m => m.marker_exists).map(m => m.sb_id);
    if (selectedCMMRows.size === matchedIds.length) {
      setSelectedCMMRows(new Set());
    } else {
      setSelectedCMMRows(new Set(matchedIds));
    }
  };

  // Apply selected CMM measurements
  const handleApplyCMMMeasurements = async () => {
    const toApply = cmmMeasurements.filter(m => selectedCMMRows.has(m.sb_id));
    
    if (toApply.length === 0) {
      toast.warning('Nessuna misura selezionata');
      return;
    }
    
    setApplyingCMM(true);
    try {
      const response = await projectsAPI.applyCMMMeasurements(projectId, toApply);
      const { updated_count, errors } = response.data;
      
      toast.success(`Aggiornati ${updated_count} marker`);
      if (errors && errors.length > 0) {
        console.warn('CMM apply errors:', errors);
      }
      
      setShowCMMPreview(false);
      setCmmMeasurements([]);
      loadProject();  // Reload to get updated markers
    } catch (error) {
      toast.error('Errore nell\'applicazione: ' + (error.response?.data?.detail || 'Errore'));
    } finally {
      setApplyingCMM(false);
    }
  };

  // Apply multi-sample CMM measurements
  const handleApplyCMMMeasurementsMultiSample = async () => {
    const toApply = cmmMeasurements.filter(m => selectedCMMRows.has(m.sb_id) && m.sample_values);
    
    if (toApply.length === 0) {
      toast.warning('Nessuna misura selezionata');
      return;
    }
    
    setApplyingCMM(true);
    try {
      const response = await projectsAPI.applyCMMMeasurementsMultiSample(projectId, toApply);
      const { updated_count, errors } = response.data;
      
      toast.success(`Aggiornati ${updated_count} marker con campioni multipli`);
      if (errors && errors.length > 0) {
        console.warn('CMM multi-sample apply errors:', errors);
      }
      
      setShowCMMPreview(false);
      setCmmMeasurements([]);
      loadProject();  // Reload to get updated markers
    } catch (error) {
      toast.error('Errore nell\'applicazione: ' + (error.response?.data?.detail || 'Errore'));
    } finally {
      setApplyingCMM(false);
    }
  };

  // ============== CMM MULTIPLE REPORTS FUNCTIONS ==============
  
  // Upload multiple CMM Reports (no limit)
  const onDropCMMReportsMultiple = useCallback(async (files) => {
    if (files.length === 0) return;
    
    // Validate all are PDFs
    const invalidFiles = files.filter(f => f.type !== 'application/pdf');
    if (invalidFiles.length > 0) {
      toast.error(`Solo file PDF accettati. File non validi: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }
    
    setUploadingCMMReports(true);
    try {
      const response = await projectsAPI.uploadCMMReportsMultiple(projectId, files);
      const { uploaded, total_count } = response.data;
      
      // Reload the list
      const reportsResponse = await projectsAPI.getCMMReportsList(projectId);
      setCmmReports(reportsResponse.data.reports || []);
      
      toast.success(`${uploaded.length} report caricati (${total_count}/5 totali)`);
    } catch (error) {
      toast.error('Upload fallito: ' + (error.response?.data?.detail || 'Errore'));
    } finally {
      setUploadingCMMReports(false);
    }
  }, [projectId, cmmReports.length]);

  const { getRootProps: getCMMMultiRootProps, getInputProps: getCMMMultiInputProps } = useDropzone({
    onDrop: onDropCMMReportsMultiple,
    accept: { 'application/pdf': ['.pdf'] },
    disabled: uploadingCMMReports
  });

  // Delete single CMM Report
  const handleDeleteSingleCMMReport = async (reportId) => {
    try {
      await projectsAPI.deleteSingleCMMReport(projectId, reportId);
      setCmmReports(prev => prev.filter(r => r.id !== reportId));
      toast.success('Report rimosso');
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  // Parse all CMM Reports - show mode selection if multiple reports
  const handleParseCMMReportsMultiple = async () => {
    // If multiple reports, ask user how to interpret them
    if (cmmReports.length > 1) {
      setCmmImportMode('manual_assign');  // Default to manual
      setShowCMMImportModeDialog(true);
      return;
    }
    // Single report - proceed directly with duplicate mode (existing behavior)
    await executeCMMParsing('duplicate');
  };

  // Execute CMM parsing with manual assignments
  const executeCMMParsingWithAssignments = async () => {
    setShowCMMManualAssignDialog(false);
    setParsingCMMReport(true);
    
    try {
      // Pass the custom assignments to the backend
      const response = await projectsAPI.parseCMMReportsMultiple(projectId, 'manual_assign', cmmReportAssignments);
      const { measurements, duplicates, stats, sample_names } = response.data;
      
      // Check for duplicates (same SB ID with multiple values in same piece)
      if (duplicates && duplicates.length > 0) {
        // Need to resolve duplicates first
        setCmmDuplicates(duplicates);
        setCmmMeasurements(measurements);
        setCmmStats({
          ...stats,
          sample_names: sample_names
        });
        setCurrentDuplicateIndex(0);
        setResolvedDuplicates({});
        setShowDuplicateDialog(true);
        toast.info(`Trovati ${duplicates.length} valori duplicati da risolvere`);
        return;
      }
      
      if (measurements.length === 0) {
        toast.warning('Nessun identificatore SB trovato nei report');
        return;
      }
      
      setCmmMeasurements(measurements);
      setCmmStats({
        ...stats,
        sample_names: sample_names
      });
      
      setSelectedCMMRows(new Set(
        measurements.filter(m => m.marker_exists).map(m => m.sb_id)
      ));
      setShowCMMPreview(true);
      toast.success(`Trovati ${measurements.length} marker con ${stats.samples_count} pezzi (${sample_names.join(', ')})`);
    } catch (error) {
      toast.error('Errore nel parsing: ' + (error.response?.data?.detail || 'Errore'));
    } finally {
      setParsingCMMReport(false);
    }
  };

  // Execute CMM parsing with selected mode
  const executeCMMParsing = async (mode) => {
    setShowCMMImportModeDialog(false);
    setParsingCMMReport(true);
    
    try {
      const response = await projectsAPI.parseCMMReportsMultiple(projectId, mode);
      const { measurements, duplicates, stats, samples } = response.data;
      
      if (measurements.length === 0 && (!duplicates || duplicates.length === 0)) {
        toast.warning('Nessun identificatore SB trovato nei report');
        return;
      }
      
      setCmmMeasurements(measurements);
      setCmmStats(stats);
      
      if (mode === 'multi_sample') {
        // Multi-sample mode: no duplicate resolution needed
        // Each report = one sample, show preview directly
        setSelectedCMMRows(new Set(
          measurements.filter(m => m.marker_exists).map(m => m.sb_id)
        ));
        setShowCMMPreview(true);
        toast.success(`Trovati ${measurements.length} marker con ${samples || cmmReports.length} campioni`);
      } else {
        // Duplicate mode (existing behavior)
        if (duplicates && duplicates.length > 0) {
          setCmmDuplicates(duplicates);
          setCurrentDuplicateIndex(0);
          setResolvedDuplicates({});
          setShowDuplicateDialog(true);
          toast.info(`Trovate ${duplicates.length} quote duplicate da risolvere`);
        } else {
          setSelectedCMMRows(new Set(
            measurements.filter(m => m.marker_exists).map(m => m.sb_id)
          ));
          setShowCMMPreview(true);
          toast.success(`Trovati ${measurements.length} valori SB da ${stats.reports_parsed} report`);
        }
      }
    } catch (error) {
      toast.error('Errore nel parsing: ' + (error.response?.data?.detail || 'Errore'));
    } finally {
      setParsingCMMReport(false);
    }
  };

  // Handle duplicate resolution selection
  const handleSelectDuplicate = (sbId, selectedMeasurement) => {
    setResolvedDuplicates(prev => ({
      ...prev,
      [sbId]: selectedMeasurement
    }));
  };

  // Move to next duplicate or finish
  const handleNextDuplicate = () => {
    const currentDup = cmmDuplicates[currentDuplicateIndex];
    
    // Check if current is resolved
    if (!resolvedDuplicates[currentDup.sb_id]) {
      toast.warning('Seleziona quale valore mantenere prima di procedere');
      return;
    }
    
    if (currentDuplicateIndex < cmmDuplicates.length - 1) {
      // More duplicates to resolve
      setCurrentDuplicateIndex(prev => prev + 1);
    } else {
      // All resolved - merge resolved values into measurements
      // For multi-sample mode: add resolved values as sample_values to existing measurements
      const measurementsBySbId = {};
      
      // First, collect existing measurements
      for (const m of cmmMeasurements) {
        measurementsBySbId[m.sb_id] = { ...m };
      }
      
      // Then add resolved duplicate values
      for (const dup of cmmDuplicates) {
        const resolved = resolvedDuplicates[dup.sb_id];
        if (!resolved) continue;
        
        if (!measurementsBySbId[dup.sb_id]) {
          // Create new measurement entry for this SB ID
          measurementsBySbId[dup.sb_id] = {
            sb_id: dup.sb_id,
            sample_values: [],
            marker_exists: dup.marker_exists,
            marker_description: dup.marker_description,
            current_value: dup.current_value
          };
        }
        
        // Add the resolved value to sample_values
        measurementsBySbId[dup.sb_id].sample_values = measurementsBySbId[dup.sb_id].sample_values || [];
        measurementsBySbId[dup.sb_id].sample_values.push({
          sample: dup.sample_num,
          sample_name: dup.sample_name,
          value: resolved.measured_value,
          filename: resolved.filename
        });
      }
      
      // Convert back to array and sort
      const allMeasurements = Object.values(measurementsBySbId);
      
      // Sort measurements by SB ID and sample_values by sample number
      allMeasurements.sort((a, b) => a.sb_id - b.sb_id);
      for (const m of allMeasurements) {
        if (m.sample_values) {
          m.sample_values.sort((a, b) => a.sample - b.sample);
        }
      }
      
      setCmmMeasurements(allMeasurements);
      setSelectedCMMRows(new Set(
        allMeasurements.filter(m => m.marker_exists).map(m => m.sb_id)
      ));
      
      setShowDuplicateDialog(false);
      setShowCMMPreview(true);
      toast.success(`Pronto per importare ${allMeasurements.length} misure`);
    }
  };

  // Go back to previous duplicate
  const handlePrevDuplicate = () => {
    if (currentDuplicateIndex > 0) {
      setCurrentDuplicateIndex(prev => prev - 1);
    }
  };

  // Auto-save markers with retry for transient errors
  const saveMarkers = useCallback(async (newMarkers, retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000; // 2 seconds
    
    setSaving(true);
    try {
      // Sanitize markers before saving - replace NaN with null
      const sanitizedMarkers = newMarkers.map(m => ({
        ...m,
        nominal: (m.nominal !== null && !isNaN(m.nominal)) ? m.nominal : null,
        tol_plus: (m.tol_plus !== null && !isNaN(m.tol_plus)) ? m.tol_plus : null,
        tol_minus: (m.tol_minus !== null && !isNaN(m.tol_minus)) ? m.tol_minus : null,
        x: isNaN(m.x) ? 0.5 : m.x,
        y: isNaN(m.y) ? 0.5 : m.y
      }));
      
      await projectsAPI.update(projectId, { markers: sanitizedMarkers });
      setHasUnsavedChanges(false);  // Reset unsaved flag after successful save
    } catch (error) {
      console.error('[SaveMarkers] Error:', error.response?.data || error.message);
      const status = error.response?.status;
      
      // Handle transient errors (502, 503) with retry
      if ((status === 502 || status === 503) && retryCount < MAX_RETRIES) {
        console.log(`[SaveMarkers] Transient error (${status}), retrying in ${RETRY_DELAY}ms... (attempt ${retryCount + 1}/${MAX_RETRIES})`);
        toast.warning(`Database temporaneamente non disponibile. Riprovo... (${retryCount + 1}/${MAX_RETRIES})`);
        setSaving(false);
        
        // Wait and retry
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return saveMarkers(newMarkers, retryCount + 1);
      }
      
      // Permanent errors
      if (error.response?.status === 403) {
        toast.error(t('marker_limit'));
      } else if (status === 502 || status === 503) {
        toast.error('Database temporaneamente non disponibile. Riprova tra qualche secondo.');
      } else {
        toast.error('Failed to save');
      }
    } finally {
      setSaving(false);
    }
  }, [projectId, t]);

  // File upload handlers
  const onDropDrawing = useCallback(async (files) => {
    if (files.length === 0) return;
    const file = files[0];
    
    setUploadingDrawing(true);
    try {
      console.log('[Upload] Uploading file:', file.name, file.size, 'bytes');
      toast.info('Caricamento in corso... Attendere prego', { duration: 60000, id: 'upload-progress' });
      
      const response = await projectsAPI.uploadDrawing(projectId, file);
      const result = response.data;
      
      toast.dismiss('upload-progress');
      
      // Check for quality warning
      if (result.quality_warning) {
        const details = result.quality_details || {};
        let warningMessage = t('quality_warning_title') || 'Attenzione: Qualità Ridotta';
        
        if (details.reason === 'file_too_large') {
          warningMessage = `${t('quality_warning_large_file') || 'Il file è molto grande. La risoluzione è stata ridotta per consentire il caricamento.'}\n\n` +
            `• Scala originale: ${details.original_scale}x\n` +
            `• Scala applicata: ${details.applied_scale}x\n\n` +
            `${t('quality_warning_ocr_note') || 'La lettura automatica delle quote potrebbe risultare meno precisa. Per risultati ottimali, considera di usare un PDF a risoluzione inferiore o inserire i valori manualmente.'}`;
        } else if (details.reason === 'image_too_large') {
          warningMessage = `${t('quality_warning_large_image') || 'L\'immagine è stata ridimensionata per consentire il caricamento.'}\n\n` +
            `• Dimensione originale: ${details.original_size}\n` +
            `• Dimensione finale: ${details.final_size}\n` +
            `• Riduzione: ${details.reduction_percent}%\n\n` +
            `${t('quality_warning_ocr_note') || 'La lettura automatica delle quote potrebbe risultare meno precisa.'}`;
        }
        
        // Show warning toast (longer duration)
        toast.warning(warningMessage, {
          duration: 10000,  // 10 seconds
          style: { whiteSpace: 'pre-line' }
        });
      }
      
      // Show tiling info if applicable
      if (result.is_tiled && result.tiles_count) {
        toast.success(`Disegno diviso in ${result.tiles_count} sezioni ad alta risoluzione`, { duration: 5000 });
      } else {
        toast.success(t('upload_success'));
      }
      
      loadProject();
      loadVectorInfo();  // Re-check vector PDF status after upload
      // Show instructions after upload
      setShowInstructions(true);
    } catch (error) {
      toast.dismiss('upload-progress');
      console.error('[Upload] Error:', error.response?.data || error.message);
      const errorDetail = error.response?.data?.detail || error.message;
      
      // Handle specific error for file too large
      if (error.response?.status === 413) {
        toast.error(t('upload_error_too_large') || `File troppo grande: ${errorDetail}`);
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error(t('upload_error_timeout') || 'Timeout durante il caricamento. Il file potrebbe essere troppo grande.');
      } else {
        toast.error('Upload failed: ' + errorDetail);
      }
    } finally {
      setUploadingDrawing(false);
    }
  }, [projectId, t]);

  const onDropLogo = useCallback(async (files) => {
    if (files.length === 0) return;
    const file = files[0];
    
    try {
      await projectsAPI.uploadLogo(projectId, file);
      toast.success(t('upload_success'));
      loadProject();
    } catch (error) {
      toast.error('Upload failed');
    }
  }, [projectId, t]);

  const { getRootProps: getDrawingRootProps, getInputProps: getDrawingInputProps } = useDropzone({
    onDrop: onDropDrawing,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg']
    },
    maxFiles: 1
  });

  const { getRootProps: getLogoRootProps, getInputProps: getLogoInputProps } = useDropzone({
    onDrop: onDropLogo,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg']
    },
    maxFiles: 1
  });

  // Canvas click handler - user clicks on dimension, system reads and places marker
  const handleCanvasClick = async (e) => {
    if (!project?.drawing_base64) return;
    
    // Get coordinates accounting for rotation
    const coords = getImagePercentCoordinates(e.clientX, e.clientY);
    if (!coords) return;
    
    // Convert from percentage (0-100) to ratio (0-1)
    const x = coords.x / 100;
    const y = coords.y / 100;
    
    // Validate that click is within the image bounds
    if (x < 0 || x > 1 || y < 0 || y > 1) {
      return;
    }
    
    // PASTE MODE - paste copied marker at click position
    if (tool === 'paste' && copiedMarker) {
      pasteMarkerAt(x, y);
      return;
    }
    
    // Check plan limits
    const userPlan = user?.plan || 'FREE';
    const maxMarkers = userPlan === 'FREE' ? 5 : 999999;
    
    if (markers.length >= maxMarkers) {
      toast.error(t('marker_limit'));
      return;
    }
    
    // Manual marker mode - just record position and open dialog
    if (tool === 'manual') {
      setPendingManualPosition({ x, y });
      setManualMarkerData({ nominal: '', tol_plus: '', tol_minus: '' });
      setShowManualMarkerDialog(true);
      return;
    }
    
    // Define View mode - create a new manual view rectangle centered on click
    if (tool === 'defineView') {
      // Don't create new view if we just finished editing one
      if (wasEditingRef.current) {
        wasEditingRef.current = false;
        return;
      }
      
      // Convert from 0-1 to percentage 0-100
      const xPercent = x * 100;
      const yPercent = y * 100;
      
      // Create rectangle 15% of image size, centered on click
      const viewSize = 15;  // 15% of image
      const halfSize = viewSize / 2;
      
      const newView = {
        id: uuidv4(),
        x1: Math.max(0, xPercent - halfSize),
        y1: Math.max(0, yPercent - halfSize),
        x2: Math.min(100, xPercent + halfSize),
        y2: Math.min(100, yPercent + halfSize),
        name: `Vista ${manualViews.length + 1}`,
        scanned: false
      };
      
      setManualViews(prev => [...prev, newView]);
      setTool('select');  // Auto-switch to select mode after creating view
      toast.success(`Vista "${newView.name}" creata. Trascina i bordi per ridimensionare.`);
      return;
    }
    
    // AI mode - read dimension at click position
    if (tool !== 'add') return;
    
    // Read dimension at click position using AI
    setReadingDimension(true);
    try {
      const response = await projectsAPI.readDimension(projectId, x, y);
      
      if (response.data.nominal !== null) {
        // Save current state for undo
        setUndoStack(prev => [...prev, [...markers]]);
        
        // Build description from API response
        // For threads and special dimensions, use the full description
        const desc = response.data.description || response.data.raw_text || String(response.data.nominal);
        
        // Create marker EXACTLY at click position (no offset)
        const newMarker = {
          id: uuidv4(),
          number: markers.length + 1,
          x: x,
          y: y,  // Position marker exactly where user clicked
          description: desc,
          nominal: response.data.nominal,
          tol_plus: response.data.tol_plus,
          tol_minus: response.data.tol_minus,
          measured: null,
          instrument: '',
          resolution: '',
          is_ok: null,
          is_ai_detected: true
        };
        
        const newMarkers = [...markers, newMarker];
        setMarkers(newMarkers);
        setSelectedMarker(newMarker);
        saveMarkers(newMarkers);
        
        // Switch to Properties tab to show the new marker details
        setActiveTab('properties');
        
        // Auto-switch back to select mode after adding marker
        setTool('select');
        
        // Show feedback with description
        if (response.data.tolerance_source) {
          toast.success(`Quota: ${desc} (${response.data.tolerance_source})`);
        } else {
          // description already contains properly formatted tolerances
          toast.success(`Quota: ${desc}`);
        }
      } else {
        toast.error('Nessuna quota trovata. Clicca direttamente sul numero della quota.');
      }
    } catch (error) {
      console.error('AI read error:', error);
      toast.error('Errore lettura quota');
    } finally {
      setReadingDimension(false);
    }
  };

  // Drag marker state
  const [draggingMarker, setDraggingMarker] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Start dragging a marker
  const handleMarkerMouseDown = (e, marker) => {
    if (tool !== 'select') return;
    e.stopPropagation();
    
    // Save state for undo BEFORE starting drag
    setUndoStack(prev => [...prev, [...markers]]);
    
    const img = imageRef.current;
    if (!img) return;
    
    const imgRect = img.getBoundingClientRect();
    const markerX = marker.x * imgRect.width;
    const markerY = marker.y * imgRect.height;
    const clickX = e.clientX - imgRect.left;
    const clickY = e.clientY - imgRect.top;
    
    setDraggingMarker(marker.id);
    setDragOffset({
      x: clickX - markerX,
      y: clickY - markerY
    });
    setSelectedMarker(marker);
  };

  // Handle marker drag
  const handleMarkerDrag = (e) => {
    if (!draggingMarker) return;
    
    const img = imageRef.current;
    if (!img) return;
    
    const imgRect = img.getBoundingClientRect();
    const newX = (e.clientX - imgRect.left - dragOffset.x) / imgRect.width;
    const newY = (e.clientY - imgRect.top - dragOffset.y) / imgRect.height;
    
    // Clamp to image bounds
    const clampedX = Math.max(0, Math.min(1, newX));
    const clampedY = Math.max(0, Math.min(1, newY));
    
    const updatedMarkers = markers.map(m => 
      m.id === draggingMarker ? { ...m, x: clampedX, y: clampedY } : m
    );
    setMarkers(updatedMarkers);
    
    // Update selected marker position
    if (selectedMarker?.id === draggingMarker) {
      setSelectedMarker({ ...selectedMarker, x: clampedX, y: clampedY });
    }
  };

  // End marker drag
  const handleMarkerDragEnd = () => {
    if (draggingMarker) {
      saveMarkers(markers);
      setDraggingMarker(null);
    }
  };

  // Analyze general tolerances from title block
  const handleAnalyzeTolerances = async () => {
    setAnalyzingTolerances(true);
    try {
      const response = await projectsAPI.analyzeTolerances(projectId);
      setGeneralTolerances(response.data);
      
      // If tolerance class found, show success
      if (response.data.tolerance_class && response.data.tolerance_class !== 'NOT_FOUND') {
        toast.success(`Tolleranze generali: ${response.data.found_text || 'ISO 2768-' + response.data.tolerance_class}`);
      } else {
        // Not found - show selection modal
        toast.info('Tolleranza non rilevata dal cartiglio. Seleziona manualmente.');
        setShowToleranceModal(true);
      }
    } catch (error) {
      toast.error('Errore analisi tolleranze');
      // Show modal for manual selection on error
      setShowToleranceModal(true);
    } finally {
      setAnalyzingTolerances(false);
    }
  };
  
  // Save manually selected tolerance class
  const handleSaveToleranceClass = async () => {
    try {
      const response = await projectsAPI.setToleranceClass(
        projectId,
        selectedToleranceClass,
        selectedToleranceClass === 'manual' ? parseFloat(manualTolerance) : null
      );
      setGeneralTolerances(response.data);
      setShowToleranceModal(false);
      toast.success(`Tolleranza impostata: ISO 2768-${selectedToleranceClass.toUpperCase()}`);
    } catch (error) {
      toast.error('Errore nel salvataggio della tolleranza');
    }
  };

  // Add another marker (quick add)
  const handleAddAnother = () => {
    setSelectedMarker(null);
    setTool('add');
  };

  // Pan handlers
  const handleMouseDown = (e) => {
    if (tool === 'select' && e.button === 0) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
  };
  
  // Real-time dimension preview on mouse move - DISABLED
  const handleDrawingMouseMove = (e) => {
    // Preview disabled - OCR was unreliable
  };
  
  // Clear preview when leaving drawing area - DISABLED
  const handleDrawingMouseLeave = () => {
    // Preview disabled
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Drag AI Suggestion to reposition
  const handleSuggestionMouseDown = (e, suggestion) => {
    if (e.button !== 0) return; // Only left click
    e.stopPropagation();
    e.preventDefault();
    setDraggingSuggestion(suggestion);
  };
  
  const handleSuggestionDrag = (e) => {
    if (!draggingSuggestion || !imageRef.current) return;
    
    const img = imageRef.current;
    const imgRect = img.getBoundingClientRect();
    
    const x = (e.clientX - imgRect.left) / imgRect.width;
    const y = (e.clientY - imgRect.top) / imgRect.height;
    
    // Update suggestion position
    setAiSuggestions(prev => prev.map(s => 
      s.id === draggingSuggestion.id 
        ? { ...s, x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) }
        : s
    ));
  };
  
  const handleSuggestionDragEnd = () => {
    setDraggingSuggestion(null);
  };

  // Zoom handlers
  const handleZoomIn = () => setZoom(z => Math.min(z * 1.2, 5));
  const handleZoomOut = () => setZoom(z => Math.max(z / 1.2, 0.2));
  const handleFitToScreen = () => {
    // Get container and image elements
    const container = canvasRef.current;
    const image = imageRef.current;
    
    if (!container || !image) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
      return;
    }
    
    const containerRect = container.getBoundingClientRect();
    
    // Get ORIGINAL image dimensions (naturalWidth/Height are the true dimensions)
    // These don't change regardless of zoom/transform
    let originalWidth = image.naturalWidth;
    let originalHeight = image.naturalHeight;
    
    // If naturalWidth not available (rare), fall back to loading from src
    if (!originalWidth || !originalHeight) {
      const tempImg = new window.Image();
      tempImg.onload = () => {
        const w = tempImg.width;
        const h = tempImg.height;
        if (w && h) {
          const padding = 60;
          const scaleX = (containerRect.width - padding) / w;
          const scaleY = (containerRect.height - padding) / h;
          const fitZoom = Math.min(scaleX, scaleY, 1);
          const scaledWidth = w * fitZoom;
          const scaledHeight = h * fitZoom;
          const centerX = (containerRect.width - scaledWidth) / 2;
          const centerY = (containerRect.height - scaledHeight) / 2;
          setZoom(fitZoom);
          setPan({ x: centerX, y: centerY });
        }
      };
      tempImg.src = image.src;
      return;
    }
    
    // Calculate zoom to fit image with padding
    const padding = 60;
    const availableWidth = containerRect.width - padding;
    const availableHeight = containerRect.height - padding;
    
    const scaleX = availableWidth / originalWidth;
    const scaleY = availableHeight / originalHeight;
    const fitZoom = Math.min(scaleX, scaleY, 1); // Max 1x (don't zoom in)
    
    // Calculate scaled dimensions
    const scaledWidth = originalWidth * fitZoom;
    const scaledHeight = originalHeight * fitZoom;
    
    // Center the image
    const centerX = (containerRect.width - scaledWidth) / 2;
    const centerY = (containerRect.height - scaledHeight) / 2;
    
    console.log(`[FitToScreen] Container: ${containerRect.width}x${containerRect.height}, Image: ${originalWidth}x${originalHeight}, FitZoom: ${fitZoom.toFixed(3)}, Center: (${centerX.toFixed(0)}, ${centerY.toFixed(0)})`);
    
    setZoom(fitZoom);
    setPan({ x: centerX, y: centerY });
  };

  // Wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    
    // Get mouse position relative to the container
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Calculate zoom factor
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.2, Math.min(5, zoom * delta));
    
    // Calculate the point in image space that the mouse is over
    const imageX = (mouseX - pan.x) / zoom;
    const imageY = (mouseY - pan.y) / zoom;
    
    // Calculate new pan to keep that point under the mouse
    const newPanX = mouseX - imageX * newZoom;
    const newPanY = mouseY - imageY * newZoom;
    
    setZoom(newZoom);
    setPan({ x: newPanX, y: newPanY });
  };

  // Marker selection
  const handleMarkerClick = (e, marker) => {
    e.stopPropagation();
    setSelectedMarker(marker);
    setTool('select');
  };

  // Update marker
  const updateMarker = (field, value) => {
    if (!selectedMarker) return;
    
    // Special handling for number field - check for duplicates
    if (field === 'number') {
      const existingMarker = markers.find(m => m.number === value && m.id !== selectedMarker.id);
      if (existingMarker) {
        // Number already exists - show confirmation dialog
        setPendingNumberChange({
          markerId: selectedMarker.id,
          newNumber: value,
          existingMarkerId: existingMarker.id
        });
        return; // Don't update yet, wait for confirmation
      }
    }
    
    const newMarkers = markers.map(m => {
      if (m.id === selectedMarker.id) {
        const updated = { ...m, [field]: value };
        
        // Auto-calculate OK/NOT OK
        // Formula: nominal + tol_minus <= measured <= nominal + tol_plus
        // tol_minus can be negative (e.g., -0.1) or positive (e.g., +0.3)
        // tol_plus can be positive (e.g., +0.7) or negative (e.g., -0.3)
        if (['measured', 'nominal', 'tol_plus', 'tol_minus'].includes(field)) {
          const measured = field === 'measured' ? value : updated.measured;
          const nominal = field === 'nominal' ? value : updated.nominal;
          const tolPlus = field === 'tol_plus' ? value : updated.tol_plus;
          const tolMinus = field === 'tol_minus' ? value : updated.tol_minus;
          
          if (measured && String(measured).toUpperCase() === 'OK') {
            updated.is_ok = true;
          } else if (measured && nominal !== null && tolPlus !== null && tolMinus !== null) {
            const measuredVal = parseFloat(measured);
            if (!isNaN(measuredVal)) {
              // Correct formula: min = nominal + tol_minus, max = nominal + tol_plus
              const minVal = nominal + tolMinus;
              const maxVal = nominal + tolPlus;
              updated.is_ok = measuredVal >= minVal && measuredVal <= maxVal;
            }
          }
        }
        
        setSelectedMarker(updated);
        return updated;
      }
      return m;
    });
    
    setMarkers(newMarkers);
    setHasUnsavedChanges(true);  // Mark as having unsaved changes
  };
  
  // Confirm marker number change and renumber other markers
  const confirmMarkerNumberChange = () => {
    if (!pendingNumberChange) return;
    
    const { markerId, newNumber } = pendingNumberChange;
    
    // Save state for undo
    setUndoStack(prev => [...prev, [...markers]]);
    
    // Renumber markers: shift all markers with number >= newNumber up by 1
    const newMarkers = markers.map(m => {
      if (m.id === markerId) {
        // This is the marker being changed - set its new number
        const updated = { ...m, number: newNumber };
        setSelectedMarker(updated);
        return updated;
      } else if (m.number >= newNumber) {
        // Shift other markers up
        return { ...m, number: m.number + 1 };
      }
      return m;
    });
    
    setMarkers(newMarkers);
    saveMarkers(newMarkers);
    setHasUnsavedChanges(true);
    setPendingNumberChange(null);
    toast.success(`Marker rinumerati. Il nuovo marker è ora #${newNumber}`);
  };
  
  // Cancel marker number change
  const cancelMarkerNumberChange = () => {
    setPendingNumberChange(null);
  };
  
  // Manual save function
  // Save all changes (markers + AI suggestions)
  const handleSaveAll = async () => {
    setSavingMarkers(true);
    try {
      // Save markers
      await saveMarkers(markers);
      
      // Save AI suggestions to project
      if (aiSuggestions.length > 0) {
        await projectsAPI.update(projectId, { ai_suggestions: aiSuggestions });
      } else {
        // Clear saved suggestions if none left
        await projectsAPI.update(projectId, { ai_suggestions: [] });
      }
      
      setHasUnsavedChanges(false);
      setHasUnsavedSuggestions(false);
      toast.success('Tutto salvato! (Markers + Suggerimenti AI)');
    } catch (error) {
      toast.error('Errore durante il salvataggio');
      console.error('Save error:', error);
    } finally {
      setSavingMarkers(false);
    }
  };

  // Legacy save markers only (for backward compatibility)
  const handleSaveMarkers = async () => {
    setSavingMarkers(true);
    try {
      await saveMarkers(markers);
      setHasUnsavedChanges(false);
      toast.success('Markers salvati!');
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    } finally {
      setSavingMarkers(false);
    }
  };

  // Delete marker - KEEP original marker numbers
  const deleteMarker = () => {
    if (!selectedMarker) return;
    
    // Save state for undo
    setUndoStack(prev => [...prev, [...markers]]);
    
    const newMarkers = markers.filter(m => m.id !== selectedMarker.id);
    
    setMarkers(newMarkers);
    setSelectedMarker(null);
    saveMarkers(newMarkers);
  };

  // Copy/Paste marker system
  const copyMarker = useCallback(() => {
    if (!selectedMarker) {
      toast.error('Seleziona prima un marker da copiare');
      return;
    }
    setCopiedMarker({ ...selectedMarker });
    setTool('paste'); // Enter paste mode
    setIsPasteMode(true);
    toast.success(`Marker ${selectedMarker.number} copiato! Clicca sul disegno per posizionarlo`);
  }, [selectedMarker]);
  
  // Paste marker at specific position
  const pasteMarkerAt = useCallback((x, y) => {
    if (!copiedMarker) {
      toast.error('Nessun marker copiato');
      return;
    }
    
    // Calculate max markers based on user plan
    const userPlan = user?.plan?.toUpperCase();
    const maxMarkers = userPlan === 'FREE' ? 5 : 999999;
    
    if (markers.length >= maxMarkers) {
      toast.error(t('marker_limit'));
      return;
    }
    
    // Save state for undo
    setUndoStack(prev => [...prev, [...markers]]);
    
    // Create new marker at the specified position
    const newMarker = {
      ...copiedMarker,
      id: uuidv4(),
      number: markers.length + 1,
      x: Math.max(0.02, Math.min(x, 0.98)),
      y: Math.max(0.02, Math.min(y, 0.98)),
    };
    
    const newMarkers = [...markers, newMarker];
    setMarkers(newMarkers);
    saveMarkers(newMarkers);
    setSelectedMarker(newMarker);
    setHasUnsavedChanges(true);
    toast.success(`Marker ${newMarker.number} incollato`);
    
    // Stay in paste mode for multiple pastes, user can press ESC or change tool to exit
  }, [copiedMarker, markers, t, saveMarkers, user]);
  
  // Exit paste mode
  const exitPasteMode = useCallback(() => {
    setIsPasteMode(false);
    setTool('select');
  }, []);
  
  // Legacy paste function (for paste button - pastes with offset from original)
  const pasteMarker = useCallback(() => {
    if (!copiedMarker) {
      toast.error('Nessun marker copiato');
      return;
    }
    
    // Paste with small offset from original position
    const offset = 0.03;
    pasteMarkerAt(
      Math.min(copiedMarker.x + offset, 0.95),
      Math.min(copiedMarker.y + offset, 0.95)
    );
  }, [copiedMarker, pasteMarkerAt]);
  
  // Keyboard shortcuts for copy/paste
  useEffect(() => {
    const handleKeyboardShortcuts = (e) => {
      // Skip if typing in input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      // ESC to exit paste mode
      if (e.key === 'Escape' && isPasteMode) {
        e.preventDefault();
        exitPasteMode();
        toast.info('Modalità incolla disattivata');
        return;
      }
      
      // Ctrl+C to copy
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedMarker) {
        e.preventDefault();
        copyMarker();
      }
      // Ctrl+V to paste (at offset position)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && copiedMarker) {
        e.preventDefault();
        if (!isPasteMode) {
          // If not in paste mode, enter paste mode
          setTool('paste');
          setIsPasteMode(true);
          toast.success('Clicca sul disegno per posizionare il marker copiato');
        } else {
          // If already in paste mode, paste at offset
          pasteMarker();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyboardShortcuts);
    return () => window.removeEventListener('keydown', handleKeyboardShortcuts);
  }, [selectedMarker, copiedMarker, copyMarker, pasteMarker, isPasteMode, exitPasteMode]);

  // AI Detection
  const handleAIDetect = async () => {
    const userPlan = user?.plan?.toUpperCase();
    if (!['AI', 'AI_UNLIMITED', 'AI_TRIAL'].includes(userPlan)) {
      toast.error(t('ai_premium_only'));
      return;
    }
    
    // Check limit
    if (aiUsage && aiUsage.limit !== -1 && aiUsage.count >= aiUsage.limit) {
      toast.error(`AI usage limit reached (${aiUsage.count}/${aiUsage.limit}). Upgrade to AI_UNLIMITED for unlimited access.`);
      return;
    }
    
    // Switch to AI tab FIRST so user sees the progress
    setActiveTab('ai');
    setAiDetecting(true);
    setAiProgress({ progress: 0, total: 100, found: 0 });
    
    try {
      // Start the AI detection job
      const startResponse = await projectsAPI.aiDetect(projectId);
      const jobId = startResponse.data.job_id;
      
      toast.info('AI detection started...', { id: 'ai-progress' });
      
      // Poll for results
      let attempts = 0;
      const maxAttempts = 600; // 20 minutes max (600 * 2 seconds) - Complex drawings need more time
      
      const pollForResults = async () => {
        while (attempts < maxAttempts) {
          attempts++;
          await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds between polls
          
          try {
            const statusResponse = await projectsAPI.aiDetectStatus(projectId, jobId);
            const { status, progress, total_sectors, found_so_far, result, error } = statusResponse.data;
            
            if (status === 'processing') {
              // Update progress state
              setAiProgress({ 
                progress: progress || 0, 
                total: total_sectors || 100, 
                found: found_so_far || 0 
              });
            } else if (status === 'completed') {
              setAiProgress({ progress: 100, total: 100, found: result.suggested_markers?.length || 0 });
              setAiSuggestions(result.suggested_markers || []);
              const creditsUsed = result.credits_used || result.api_calls || 1;
              if (result.suggested_markers?.length > 0) {
                toast.success(`Trovate ${result.suggested_markers.length} quote! (${creditsUsed} crediti)`, { id: 'ai-progress' });
              } else {
                toast.info(`Nessuna quota rilevata (${creditsUsed} crediti usati)`, { id: 'ai-progress' });
              }
              // Reload AI usage after successful detection
              loadAiUsage();
              return;
            } else if (status === 'error' || status === 'failed') {
              toast.error(error || 'Rilevamento AI fallito', { id: 'ai-progress' });
              loadAiUsage(); // Reload usage even on error (1 credit charged)
              return;
            }
          } catch (pollError) {
            console.error('Polling error:', pollError);
            // Continue polling even if one request fails
          }
        }
        
        toast.error('Scansione AI scaduta - riprova', { id: 'ai-progress' });
      };
      
      await pollForResults();
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Rilevamento AI fallito';
      if (errorMsg.includes('limit reached')) {
        toast.error(errorMsg);
        loadAiUsage(); // Refresh usage display
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setAiDetecting(false);
    }
  };

  // NEW: View-by-View Detection Functions
  const handleDetectViews = async () => {
    const userPlan = user?.plan?.toUpperCase();
    if (!['AI', 'AI_UNLIMITED', 'AI_TRIAL'].includes(userPlan)) {
      toast.error(t('ai_premium_only'));
      return;
    }
    
    if (aiUsage && aiUsage.limit !== -1 && aiUsage.count >= aiUsage.limit) {
      toast.error(`Limite AI raggiunto (${aiUsage.count}/${aiUsage.limit})`);
      return;
    }
    
    setDetectingViews(true);
    setActiveTab('ai');
    
    try {
      const response = await aiAPI.detectViews(projectId);
      const { views, credits_used } = response.data;
      
      setDetectedViews(views || []);
      setScannedViews([]);
      
      toast.success(`Rilevate ${views.length} viste! (${credits_used} credito)`, { duration: 3000 });
      loadAiUsage();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Errore nel rilevamento viste';
      toast.error(errorMsg);
    } finally {
      setDetectingViews(false);
    }
  };

  // Multi-pass tiled detection (ASYNC with polling)
  const handleDetectAllTiled = async () => {
    const userPlan = user?.plan?.toUpperCase() || 'FREE';
    if (!['AI', 'AI_UNLIMITED', 'AI_TRIAL'].includes(userPlan)) {
      toast.error(t('ai_premium_only'));
      return;
    }
    
    if (!window.confirm(
      '🔍 RILEVAMENTO MULTI-PASS (Consigliato)\n\n' +
      'Il disegno verrà analizzato in più sezioni ad alta risoluzione.\n\n' +
      '✅ Migliore copertura su disegni densi\n' +
      '✅ Maggiore precisione su testo piccolo\n' +
      '⚠️ Usa ~18 crediti AI (9 sezioni x 2)\n' +
      '⏱️ Potrebbe richiedere alcuni minuti\n\n' +
      'Vuoi procedere?'
    )) {
      return;
    }
    
    setDetectingMultiPass(true);
    setActiveTab('ai');
    
    try {
      // Start the async job
      const startResponse = await aiAPI.startMultipass(projectId, {
        grid_size: 3,
        overlap_percent: 20,
        tile_index: activeTileIndex
      });
      
      const jobId = startResponse.data.job_id;
      toast.info('🔍 Analisi multi-pass avviata...', { duration: 10000, id: 'multipass-progress' });
      
      // Poll for status
      let completed = false;
      let attempts = 0;
      const maxAttempts = 180;  // 3 minutes max with 1s interval
      
      while (!completed && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 1000));  // 1 second delay
        attempts++;
        
        try {
          const statusResponse = await aiAPI.getMultipassStatus(projectId, jobId);
          const status = statusResponse.data;
          
          // Update progress toast
          if (status.progress && status.progress < 100) {
            const tilesInfo = status.total_tiles ? ` (${status.processed_tiles}/${status.total_tiles} sezioni)` : '';
            toast.loading(`🔍 Analisi: ${status.progress}%${tilesInfo}`, { id: 'multipass-progress' });
          }
          
          if (status.status === 'completed') {
            completed = true;
            toast.dismiss('multipass-progress');
            
            const { dimensions, count, tiles_processed, duplicates_removed, credits_used } = status;
            
            if (dimensions && dimensions.length > 0) {
              const newSuggestions = dimensions.map((d) => ({
                id: d.id,
                x: d.x,
                y: d.y,
                nominal: d.nominal,
                nominal_str: d.nominal_str,
                tol_plus: d.tol_plus,
                tol_minus: d.tol_minus,
                description: d.description,
                type: d.type,
                is_ai_detected: true
              }));
              
              setAiSuggestions(prev => [...prev, ...newSuggestions]);
              toast.success(
                `🎉 Trovate ${count} quote!\n` +
                `Analizzate ${tiles_processed} sezioni, ${duplicates_removed} duplicati rimossi\n` +
                `Crediti usati: ${credits_used}`,
                { duration: 7000 }
              );
            } else {
              toast.info('Nessuna quota trovata');
            }
            
            loadAiUsage();
          } else if (status.status === 'error') {
            completed = true;
            toast.dismiss('multipass-progress');
            toast.error(status.error || 'Errore durante l\'analisi');
          }
        } catch (pollError) {
          // Continue polling on network errors
          console.log('Polling error:', pollError);
        }
      }
      
      if (!completed) {
        toast.dismiss('multipass-progress');
        toast.error('Timeout: l\'analisi sta richiedendo troppo tempo');
      }
      
    } catch (error) {
      toast.dismiss('multipass-progress');
      const errorMsg = error.response?.data?.detail || 'Errore durante l\'avvio dell\'analisi';
      toast.error(errorMsg);
    } finally {
      setDetectingMultiPass(false);
    }
  };

  // EXPERIMENTAL: Vector PDF Detection (no AI credits)
  const handleDetectVector = async () => {
    if (!vectorInfo?.feature_enabled || !vectorInfo?.is_vector_pdf) {
      toast.error('Vector detection non disponibile per questo PDF');
      return;
    }
    
    setDetectingVector(true);
    setActiveTab('ai');
    
    try {
      toast.info('🔬 Analisi AI in corso...', { duration: 60000, id: 'vector-progress' });
      
      const response = await aiAPI.detectVector(projectId);
      
      toast.dismiss('vector-progress');
      
      const { dimensions, count, credits_used, message, success } = response.data;
      
      if (success && dimensions && dimensions.length > 0) {
        const newSuggestions = dimensions.map((d) => ({
          id: d.id,
          x: d.x,
          y: d.y,
          nominal: d.nominal,
          nominal_str: d.nominal_str,
          tol_plus: d.tol_plus,
          tol_minus: d.tol_minus,
          description: d.description,
          is_ai_detected: d.is_ai_detected || true,
          is_vector_detected: true
        }));
        
        setAiSuggestions(prev => [...prev, ...newSuggestions]);
        
        // Show credits only if used
        const creditsMsg = credits_used > 0 ? ` (${credits_used} crediti AI)` : '';
        toast.success(`🔬 Estratte ${count} quote${creditsMsg}`, { duration: 5000 });
        
        // Refresh AI usage if credits were used
        if (credits_used > 0) {
          loadAiUsage();
        }
      } else {
        toast.info(message || 'Nessuna quota trovata nel testo vettoriale');
      }
    } catch (error) {
      toast.dismiss('vector-progress');
      const errorMsg = error.response?.data?.detail || 'Errore durante l\'analisi';
      toast.error(errorMsg);
    } finally {
      setDetectingVector(false);
    }
  };

  // Load vector info when project loads - auto-check if not set
  const loadVectorInfo = async () => {
    try {
      const response = await aiAPI.getVectorInfo(projectId);
      const info = response.data;
      
      console.log('[VectorInfo] Response:', info);
      
      // If feature is enabled and needs_check flag is set, or is_vector_pdf is false but has drawing
      if (info.feature_enabled && (info.needs_check || (info.is_vector_pdf === false && info.has_drawing))) {
        // Auto-check for existing PDFs
        console.log('[VectorInfo] Auto-checking PDF...');
        try {
          const checkResponse = await aiAPI.checkVector(projectId);
          console.log('[VectorInfo] Check result:', checkResponse.data);
          if (checkResponse.data.is_vector_pdf) {
            setVectorInfo({ ...info, is_vector_pdf: true });
            toast.success(`🔬 PDF vettoriale rilevato! (${checkResponse.data.text_spans} elementi)`, { duration: 4000 });
            return;
          }
        } catch (checkError) {
          console.log('[VectorInfo] Vector check failed:', checkError);
        }
      }
      
      setVectorInfo(info);
    } catch (error) {
      // Silently fail - feature may not be enabled
      console.log('Vector info not available:', error.response?.status);
    }
  };

  const handleScanSingleView = async (viewIndex) => {
    if (aiUsage && aiUsage.limit !== -1 && aiUsage.count >= aiUsage.limit) {
      toast.error(`Limite AI raggiunto (${aiUsage.count}/${aiUsage.limit})`);
      return;
    }
    
    setScanningView(viewIndex);
    
    try {
      const response = await aiAPI.scanView(projectId, viewIndex);
      const { dimensions, credits_used, view_name } = response.data;
      
      // Add dimensions to suggestions
      if (dimensions && dimensions.length > 0) {
        const newSuggestions = dimensions.map((d, i) => ({
          id: `view${viewIndex}_${i}_${Date.now()}`,
          x: d.x,
          y: d.y,
          nominal: d.nominal,
          tol_plus: d.tol_plus,
          tol_minus: d.tol_minus,
          type: d.type,
          description: d.description,
          is_ai_detected: true
        }));
        
        setAiSuggestions(prev => [...prev, ...newSuggestions]);
        toast.success(`"${view_name}": ${dimensions.length} quote (${credits_used} crediti)`, { duration: 3000 });
      } else {
        toast.info(`"${view_name}": nessuna quota trovata (${credits_used} crediti)`, { duration: 3000 });
      }
      
      // Mark view as scanned
      setScannedViews(prev => [...prev, viewIndex]);
      setDetectedViews(prev => prev.map((v, i) => i === viewIndex ? { ...v, scanned: true } : v));
      
      loadAiUsage();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Errore nella scansione vista';
      toast.error(errorMsg);
    } finally {
      setScanningView(null);
    }
  };

  const handleScanAllViews = async () => {
    if (detectedViews.length === 0) return;
    
    const unscannedViews = detectedViews.filter((v, i) => !scannedViews.includes(i));
    if (unscannedViews.length === 0) {
      toast.info('Tutte le viste sono già state scansionate');
      return;
    }
    
    // Scan one view at a time with progress
    for (let i = 0; i < detectedViews.length; i++) {
      if (scannedViews.includes(i)) continue;
      
      await handleScanSingleView(i);
      
      // Small delay between views to not overwhelm the UI
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    toast.success('Scansione di tutte le viste completata!');
  };

  const clearDetectedViews = () => {
    setDetectedViews([]);
    setScannedViews([]);
    setHoveredView(null);
  };

  // ============== MANUAL VIEW FUNCTIONS ==============
  
  // Helper function to convert mouse coordinates to image percentages accounting for rotation
  const getImagePercentCoordinates = (clientX, clientY) => {
    const img = imageRef.current;
    if (!img) return null;
    
    const imgRect = img.getBoundingClientRect();
    
    // Get the center of the image in viewport coordinates
    const centerX = imgRect.left + imgRect.width / 2;
    const centerY = imgRect.top + imgRect.height / 2;
    
    // Get mouse position relative to center
    let relX = clientX - centerX;
    let relY = clientY - centerY;
    
    // If image is rotated, we need to rotate the mouse coordinates in the OPPOSITE direction
    // to get the coordinates in the original (unrotated) image space
    if (imageRotation !== 0) {
      const angleRad = -imageRotation * Math.PI / 180;  // Negative to reverse rotation
      const cos = Math.cos(angleRad);
      const sin = Math.sin(angleRad);
      const rotatedX = relX * cos - relY * sin;
      const rotatedY = relX * sin + relY * cos;
      relX = rotatedX;
      relY = rotatedY;
    }
    
    // Now calculate the actual image dimensions accounting for zoom
    const actualWidth = img.naturalWidth * zoom;
    const actualHeight = img.naturalHeight * zoom;
    
    // Convert back to percentage (0-100) of the original image
    // The center of the image is at 50%, so we adjust from there
    const percentX = 50 + (relX / actualWidth) * 100;
    const percentY = 50 + (relY / actualHeight) * 100;
    
    // Clamp to 0-100
    return {
      x: Math.max(0, Math.min(100, percentX)),
      y: Math.max(0, Math.min(100, percentY))
    };
  };
  
  const handleManualViewMouseDown = (e, viewId, handle = null) => {
    e.stopPropagation();
    e.preventDefault();
    
    const coords = getImagePercentCoordinates(e.clientX, e.clientY);
    if (!coords) return;
    
    setEditingManualView(viewId);
    setResizeHandle(handle);  // null = move, 'nw'/'ne'/'sw'/'se' = resize corners
    setDragStartPos(coords);
  };
  
  const handleManualViewMouseMove = (e) => {
    if (!editingManualView || !dragStartPos) return;
    
    const coords = getImagePercentCoordinates(e.clientX, e.clientY);
    if (!coords) return;
    
    const deltaX = coords.x - dragStartPos.x;
    const deltaY = coords.y - dragStartPos.y;
    
    setManualViews(prev => prev.map(view => {
      if (view.id !== editingManualView) return view;
      
      let newView = { ...view };
      
      if (resizeHandle === null) {
        // Move the entire rectangle
        const width = view.x2 - view.x1;
        const height = view.y2 - view.y1;
        newView.x1 = Math.max(0, Math.min(100 - width, view.x1 + deltaX));
        newView.y1 = Math.max(0, Math.min(100 - height, view.y1 + deltaY));
        newView.x2 = newView.x1 + width;
        newView.y2 = newView.y1 + height;
      } else {
        // Resize based on handle
        const minSize = 5;  // Minimum 5% size
        
        switch (resizeHandle) {
          case 'nw':
            newView.x1 = Math.max(0, Math.min(view.x2 - minSize, view.x1 + deltaX));
            newView.y1 = Math.max(0, Math.min(view.y2 - minSize, view.y1 + deltaY));
            break;
          case 'ne':
            newView.x2 = Math.min(100, Math.max(view.x1 + minSize, view.x2 + deltaX));
            newView.y1 = Math.max(0, Math.min(view.y2 - minSize, view.y1 + deltaY));
            break;
          case 'sw':
            newView.x1 = Math.max(0, Math.min(view.x2 - minSize, view.x1 + deltaX));
            newView.y2 = Math.min(100, Math.max(view.y1 + minSize, view.y2 + deltaY));
            break;
          case 'se':
            newView.x2 = Math.min(100, Math.max(view.x1 + minSize, view.x2 + deltaX));
            newView.y2 = Math.min(100, Math.max(view.y1 + minSize, view.y2 + deltaY));
            break;
          default:
            break;
        }
      }
      
      return newView;
    }));
    
    setDragStartPos(coords);
  };
  
  const handleManualViewMouseUp = () => {
    // Set flag to prevent click from creating new view
    if (editingManualView) {
      wasEditingRef.current = true;
    }
    setEditingManualView(null);
    setResizeHandle(null);
    setDragStartPos(null);
  };
  
  // ============== TOUCH SUPPORT FOR TABLETS ==============
  
  const handleManualViewTouchStart = (e, viewId, handle = null) => {
    e.stopPropagation();
    // Don't prevent default completely - allow some touch behaviors
    
    if (e.touches.length !== 1) return; // Only single touch
    
    const touch = e.touches[0];
    const coords = getImagePercentCoordinates(touch.clientX, touch.clientY);
    if (!coords) return;
    
    const imgRect = img.getBoundingClientRect();
    const startX = (touch.clientX - imgRect.left) / imgRect.width * 100;
    const startY = (touch.clientY - imgRect.top) / imgRect.height * 100;
    
    setEditingManualView(viewId);
    setResizeHandle(handle);
    setDragStartPos(coords);
  };
  
  const handleManualViewTouchMove = (e) => {
    if (!editingManualView || !dragStartPos) return;
    if (e.touches.length !== 1) return;
    
    e.preventDefault(); // Prevent scrolling while dragging
    
    const touch = e.touches[0];
    const coords = getImagePercentCoordinates(touch.clientX, touch.clientY);
    if (!coords) return;
    
    const deltaX = coords.x - dragStartPos.x;
    const deltaY = coords.y - dragStartPos.y;
    
    setManualViews(prev => prev.map(view => {
      if (view.id !== editingManualView) return view;
      
      let newView = { ...view };
      
      if (resizeHandle === null) {
        // Move the entire rectangle
        const width = view.x2 - view.x1;
        const height = view.y2 - view.y1;
        newView.x1 = Math.max(0, Math.min(100 - width, view.x1 + deltaX));
        newView.y1 = Math.max(0, Math.min(100 - height, view.y1 + deltaY));
        newView.x2 = newView.x1 + width;
        newView.y2 = newView.y1 + height;
      } else {
        // Resize based on handle
        const minSize = 5;
        
        switch (resizeHandle) {
          case 'nw':
            newView.x1 = Math.max(0, Math.min(view.x2 - minSize, view.x1 + deltaX));
            newView.y1 = Math.max(0, Math.min(view.y2 - minSize, view.y1 + deltaY));
            break;
          case 'ne':
            newView.x2 = Math.min(100, Math.max(view.x1 + minSize, view.x2 + deltaX));
            newView.y1 = Math.max(0, Math.min(view.y2 - minSize, view.y1 + deltaY));
            break;
          case 'sw':
            newView.x1 = Math.max(0, Math.min(view.x2 - minSize, view.x1 + deltaX));
            newView.y2 = Math.min(100, Math.max(view.y1 + minSize, view.y2 + deltaY));
            break;
          case 'se':
            newView.x2 = Math.min(100, Math.max(view.x1 + minSize, view.x2 + deltaX));
            newView.y2 = Math.min(100, Math.max(view.y1 + minSize, view.y2 + deltaY));
            break;
          default:
            break;
        }
      }
      
      return newView;
    }));
    
    setDragStartPos(coords);
  };
  
  const handleManualViewTouchEnd = () => {
    if (editingManualView) {
      wasEditingRef.current = true;
    }
    setEditingManualView(null);
    setResizeHandle(null);
    setDragStartPos(null);
  };
  
  const deleteManualView = (viewId) => {
    setManualViews(prev => prev.filter(v => v.id !== viewId));
  };
  
  const clearManualViews = () => {
    setManualViews([]);
  };
  
  const handleScanManualView = async (viewId) => {
    const view = manualViews.find(v => v.id === viewId);
    if (!view) return;
    
    if (aiUsage && aiUsage.limit !== -1 && aiUsage.count >= aiUsage.limit) {
      toast.error(`Limite AI raggiunto (${aiUsage.count}/${aiUsage.limit})`);
      return;
    }
    
    setScanningManualView(viewId);
    
    try {
      // Get default tolerance from general tolerances if available
      let defaultTolerance = null;
      if (generalTolerances && generalTolerances.linear) {
        // Use the first value from linear tolerances as default
        defaultTolerance = Object.values(generalTolerances.linear)[0] || 0.1;
      }
      
      const response = await aiAPI.scanCustomArea(projectId, {
        x1: view.x1,
        y1: view.y1,
        x2: view.x2,
        y2: view.y2,
        name: view.name,
        default_tolerance: defaultTolerance,
        scan_rotated: scanRotated,  // Option to scan rotated for vertical text
        deduplicate: true,  // Remove duplicates automatically
        tile_index: activeTileIndex  // Pass tile index if viewing a specific tile
      });
      
      const { dimensions, credits_used, view_name } = response.data;
      
      if (dimensions && dimensions.length > 0) {
        const newSuggestions = dimensions.map((d, i) => ({
          id: `manual_${viewId}_${i}_${Date.now()}`,
          x: d.x / 100,  // Convert from percentage to 0-1
          y: d.y / 100,
          nominal: d.nominal,
          nominal_str: d.nominal_str || String(d.nominal),  // Keep M10, G1/4, etc.
          tol_plus: d.tol_plus,
          tol_minus: d.tol_minus,
          type: d.type,
          description: d.description,
          is_ai_detected: true,
          has_explicit_tolerance: d.has_explicit_tolerance
        }));
        
        setAiSuggestions(prev => [...prev, ...newSuggestions]);
        toast.success(`"${view_name}": ${dimensions.length} quote (${credits_used} crediti)`, { duration: 3000 });
      } else {
        toast.info(`"${view_name}": nessuna quota trovata (${credits_used} crediti)`, { duration: 3000 });
      }
      
      // Mark view as scanned
      setManualViews(prev => prev.map(v => v.id === viewId ? { ...v, scanned: true } : v));
      
      // Switch back to select tool to allow pan/zoom
      setTool('select');
      
      loadAiUsage();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Errore nella scansione';
      toast.error(errorMsg);
    } finally {
      setScanningManualView(null);
    }
  };
  
  const handleScanAllManualViews = async () => {
    const unscannedViews = manualViews.filter(v => !v.scanned);
    if (unscannedViews.length === 0) {
      toast.info('Tutte le viste manuali sono già state scansionate');
      return;
    }
    
    for (const view of unscannedViews) {
      await handleScanManualView(view.id);
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    toast.success('Scansione di tutte le viste manuali completata!');
  };

  const acceptAISuggestion = (suggestion) => {
    // Use marker_number if specified, otherwise auto-increment
    const assignedNumber = suggestion.marker_number || (markers.length + 1);
    
    // Check if number already exists
    const existingMarker = markers.find(m => m.number === assignedNumber);
    if (existingMarker) {
      // Show confirmation dialog for renumbering
      setPendingAISuggestionAccept({
        suggestion: suggestion,
        newNumber: assignedNumber
      });
      return; // Wait for confirmation
    }
    
    // No conflict - proceed with acceptance
    doAcceptAISuggestion(suggestion, assignedNumber);
  };
  
  // Actually accept the AI suggestion (after confirmation if needed)
  const doAcceptAISuggestion = (suggestion, assignedNumber) => {
    // Save current state for undo
    setUndoStack(prev => [...prev, [...markers]]);
    
    // Create new marker with a fresh UUID (AI suggestions may have non-UUID ids)
    const newMarker = {
      ...suggestion,
      id: uuidv4(),  // Generate valid UUID for database
      number: assignedNumber
    };
    const newMarkers = [...markers, newMarker];
    setMarkers(newMarkers);
    setAiSuggestions(aiSuggestions.filter(s => s.id !== suggestion.id));
    saveMarkers(newMarkers);
    
    // Auto-switch back to select mode after accepting
    setTool('select');
    setIsPasteMode(false);
  };
  
  // Confirm AI suggestion with renumbering
  const confirmAISuggestionWithRenumber = () => {
    if (!pendingAISuggestionAccept) return;
    
    const { suggestion, newNumber } = pendingAISuggestionAccept;
    
    // Save current state for undo
    setUndoStack(prev => [...prev, [...markers]]);
    
    // Renumber existing markers first
    const renumberedMarkers = markers.map(m => {
      if (m.number >= newNumber) {
        return { ...m, number: m.number + 1 };
      }
      return m;
    });
    
    // Create new marker
    const newMarker = {
      ...suggestion,
      id: uuidv4(),
      number: newNumber
    };
    
    const newMarkers = [...renumberedMarkers, newMarker];
    setMarkers(newMarkers);
    setAiSuggestions(aiSuggestions.filter(s => s.id !== suggestion.id));
    saveMarkers(newMarkers);
    
    // Auto-switch back to select mode
    setTool('select');
    setIsPasteMode(false);
    setSelectedSuggestion(null);
    
    setPendingAISuggestionAccept(null);
    toast.success(`Marker #${newNumber} inserito, altri rinumerati`);
  };

  // Zoom and center on a specific marker/suggestion
  const zoomToMarker = (marker) => {
    if (!imageRef.current || !canvasRef.current) return;
    
    const img = imageRef.current;
    const container = canvasRef.current;  // Use the canvas container, not image parent
    
    const containerRect = container.getBoundingClientRect();
    
    // Set zoom to 2x for good visibility
    const newZoom = 2;
    setZoom(newZoom);
    
    // Calculate the position to center the marker
    const markerX = marker.x * img.naturalWidth * newZoom;
    const markerY = marker.y * img.naturalHeight * newZoom;
    
    // Center the marker in the container
    const newPanX = (containerRect.width / 2) - markerX;
    const newPanY = (containerRect.height / 2) - markerY;
    
    setPan({ x: newPanX, y: newPanY });
    
    // Highlight the marker
    setHighlightedMarkerId(marker.id);
    
    // Clear highlight after 3 seconds
    setTimeout(() => {
      setHighlightedMarkerId(null);
    }, 3000);
  };

  const acceptAllSuggestions = () => {
    let nextNumber = markers.length + 1;
    const newMarkers = [
      ...markers,
      ...aiSuggestions.map(s => ({ 
        ...s, 
        id: uuidv4(),  // Generate valid UUID for each suggestion
        number: nextNumber++ 
      }))
    ];
    setMarkers(newMarkers);
    setAiSuggestions([]);
    saveMarkers(newMarkers);
  };

  // Export handlers
  const handleExportPDF = async () => {
    // Block export for Trial users
    if (isTrial) {
      setTrialExportModalOpen(true);
      return;
    }
    
    setExporting(true);
    let retries = 2;
    
    while (retries >= 0) {
      try {
        const response = await projectsAPI.exportPDF(projectId);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${project.name}_ballooned.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(t('export_started'));
        break;
      } catch (error) {
        if (retries > 0) {
          console.log(`[ExportPDF] Retry ${2 - retries + 1}/2...`);
          retries--;
          await new Promise(r => setTimeout(r, 1000)); // Wait 1 second before retry
        } else {
          toast.error('Export failed');
        }
      }
    }
    setExporting(false);
  };

  const handleExportExcel = async () => {
    // Block export for Trial users
    if (isTrial) {
      setTrialExportModalOpen(true);
      return;
    }
    
    setExporting(true);
    try {
      // Pass current language to export endpoint
      const currentLang = i18n.language?.substring(0, 2) || 'it';
      const response = await projectsAPI.exportExcel(projectId, currentLang);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${project.name}_ISIR.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('export_started'));
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Toolbar */}
      <header className="h-14 border-b border-slate-200 bg-white flex items-center px-4 justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/dashboard')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('dashboard')}
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/guide')}
            className="text-slate-500 hover:text-slate-700"
            data-testid="guide-link"
          >
            <HelpCircle className="w-4 h-4 mr-1" />
            {t('guide')}
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/terms')}
            className="text-slate-500 hover:text-slate-700"
            data-testid="terms-link"
          >
            {t('terms_of_use')}
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <h1 className="font-bold text-slate-900 truncate max-w-[200px]">
            {project?.name}
          </h1>
          {saving && <span className="text-xs text-slate-400">Saving...</span>}
        </div>

        <div className="flex items-center gap-2">
          {/* Tools with descriptions */}
          <div className="flex items-center gap-1">
            <button
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                tool === 'select' 
                  ? 'bg-primary text-white' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              onClick={() => setTool('select')}
              data-testid="tool-select"
            >
              <MousePointer className="w-4 h-4" />
              <span>{t('select_move')}</span>
            </button>
            <button
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                tool === 'add' 
                  ? 'bg-primary text-white' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              onClick={() => {
                setTool('add');
                setActiveTab('properties');  // Always switch to properties when selecting Marker AI
              }}
              data-testid="tool-add"
            >
              <Sparkles className="w-4 h-4" />
              <span>{t('marker_ai')}</span>
            </button>
            <button
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                tool === 'manual' 
                  ? 'bg-amber-500 text-white' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              onClick={() => setTool('manual')}
              data-testid="tool-manual"
            >
              <Plus className="w-4 h-4" />
              <span>{t('manual_marker')}</span>
            </button>
            {/* Paste Mode Indicator - only shown when a marker is copied */}
            {copiedMarker && (
              <button
                className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  tool === 'paste' 
                    ? 'bg-green-500 text-white animate-pulse' 
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
                onClick={() => {
                  if (tool === 'paste') {
                    exitPasteMode();
                  } else {
                    setTool('paste');
                    setIsPasteMode(true);
                  }
                }}
                data-testid="tool-paste"
                title="Clicca sul disegno per incollare il marker copiato (ESC per uscire)"
              >
                <ClipboardPaste className="w-4 h-4" />
                <span>{tool === 'paste' ? 'Clicca per incollare...' : `Incolla #${copiedMarker.number}`}</span>
              </button>
            )}
            <button
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                tool === 'defineView' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              onClick={() => {
                setTool('defineView');
                setActiveTab('ai');
              }}
              disabled={!['AI', 'AI_UNLIMITED', 'AI_TRIAL'].includes(user?.plan?.toUpperCase())}
              title={!['AI', 'AI_UNLIMITED', 'AI_TRIAL'].includes(user?.plan?.toUpperCase()) ? t('ai_premium_only') : t('define_view')}
              data-testid="tool-define-view"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" strokeDasharray="4 2" />
                <circle cx="12" cy="12" r="2" fill="currentColor" />
              </svg>
              <span>{t('define_view')}</span>
            </button>
            
            {/* UNIFIED AI Detection Button - automatically chooses Vector or Multi-Pass */}
            {['AI', 'AI_UNLIMITED', 'AI_TRIAL'].includes(user?.plan?.toUpperCase()) && (
              <button
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all ${
                  (detectingMultiPass || detectingVector)
                    ? 'bg-blue-100 text-blue-700 cursor-wait'
                    : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600 shadow-sm'
                }`}
                onClick={() => {
                  // If there are already AI suggestions OR confirmed markers, ask for confirmation
                  const hasExistingContent = aiSuggestions.length > 0 || markers.length > 0;
                  console.log('[Detect AI] Click - aiSuggestions:', aiSuggestions.length, 'markers:', markers.length, 'showConfirm:', hasExistingContent);
                  if (hasExistingContent) {
                    setShowRescanConfirm(true);
                    return;
                  }
                  // If vector PDF, use vector detection (free, no credits)
                  // Otherwise, use multi-pass AI detection
                  if (vectorInfo?.is_vector_pdf) {
                    handleDetectVector();
                  } else {
                    handleDetectAllTiled();
                  }
                }}
                disabled={detectingMultiPass || detectingVector || !project?.drawing_base64}
                title={vectorInfo?.is_vector_pdf 
                  ? "PDF vettoriale - estrazione + raffinamento AI" 
                  : t('detect_dimensions_ai')}
                data-testid="tool-detect-ai"
              >
                {(detectingMultiPass || detectingVector) ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{t('ai_detecting')}</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-5 0v-15A2.5 2.5 0 0 1 9.5 2z" />
                      <path d="M14.5 8A2.5 2.5 0 0 1 17 10.5v9a2.5 2.5 0 0 1-5 0v-9A2.5 2.5 0 0 1 14.5 8z" />
                    </svg>
                    <span>{t('detect_dimensions_ai')}</span>
                  </>
                )}
              </button>
            )}
            
            {/* Warning for non-vector PDFs */}
            {vectorInfo?.feature_enabled && vectorInfo?.has_drawing && !vectorInfo?.is_vector_pdf && ['AI', 'AI_UNLIMITED', 'AI_TRIAL'].includes(user?.plan?.toUpperCase()) && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-amber-50 border border-amber-200" title={t('not_vector_use_define_view')}>
                <Info className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                <span className="text-xs text-amber-700">
                  {t('not_vector_use_define_view')}
                </span>
              </div>
            )}
          </div>

          <Separator orientation="vertical" className="h-6" />

          {/* Zoom */}
          <div className="flex items-center gap-1">
            <button className="toolbar-btn" onClick={handleZoomOut} title={t('zoom_out')}>
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs text-slate-500 w-12 text-center">
              {Math.round(zoom * 100)}%
            </span>
            <button className="toolbar-btn" onClick={handleZoomIn} title={t('zoom_in')}>
              <ZoomIn className="w-4 h-4" />
            </button>
            <button className="toolbar-btn" onClick={handleFitToScreen} title={t('fit_to_screen')}>
              <Maximize className="w-4 h-4" />
            </button>
            <button 
              className="toolbar-btn" 
              onClick={() => {
                setImageRotation(prev => (prev + 90) % 360);
                // Auto-fit after rotation with a small delay to let the transform apply
                setTimeout(() => handleFitToWindow(), 50);
              }} 
              title="Ruota 90°"
            >
              <RotateCw className="w-4 h-4" />
            </button>
          </div>

          <Separator orientation="vertical" className="h-6" />

          {/* Undo button */}
          {undoStack.length > 0 && (
            <button
              className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-slate-100 text-slate-700 hover:bg-slate-200"
              onClick={() => {
                if (undoStack.length > 0) {
                  const previousState = undoStack[undoStack.length - 1];
                  setMarkers(previousState);
                  setUndoStack(prev => prev.slice(0, -1));
                  saveMarkers(previousState);
                  toast.info('Undo completato');
                }
              }}
              title="Annulla ultima azione"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              Undo
            </button>
          )}

          {/* Status indicator */}
          {readingDimension && (
            <div className="flex items-center gap-2 px-2 py-1 bg-blue-50 rounded text-xs text-blue-600">
              <Loader2 className="w-3 h-3 animate-spin" />
              Lettura quota...
            </div>
          )}

          {/* AI Usage Display */}
          {aiUsage && (
            <AIUsageDisplay 
              usage={aiUsage} 
              onUpgrade={() => navigate('/dashboard?upgrade=true')}
            />
          )}

          <Separator orientation="vertical" className="h-6" />

          {/* SAVE ALL button - prominent when there are unsaved changes */}
          <Button
            variant={hasUnsavedChanges || hasUnsavedSuggestions ? "default" : "outline"}
            size="sm"
            onClick={handleSaveAll}
            disabled={savingMarkers || (!hasUnsavedChanges && !hasUnsavedSuggestions)}
            className={hasUnsavedChanges || hasUnsavedSuggestions 
              ? "bg-green-600 hover:bg-green-700 text-white animate-pulse" 
              : ""}
            data-testid="save-all-btn"
          >
            {savingMarkers ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {hasUnsavedChanges || hasUnsavedSuggestions ? (
              <>
                SALVA TUTTO
                {aiSuggestions.length > 0 && (
                  <span className="ml-1 bg-white/20 px-1.5 py-0.5 rounded text-xs">
                    +{aiSuggestions.length} AI
                  </span>
                )}
              </>
            ) : (
              'Salvato'
            )}
          </Button>

          <Separator orientation="vertical" className="h-6" />

          {/* Export */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPDF}
            disabled={exporting}
            data-testid="export-pdf-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {exporting ? 'Generando...' : 'PDF'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportExcel}
            disabled={exporting}
            data-testid="export-excel-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 mr-2" />
            )}
            {exporting ? 'Generando...' : 'Excel'}
          </Button>
          
          {/* Help Menu */}
          <HelpMenuButton onResetTutorial={() => {
            resetTutorial();
            setShowTutorial(true);
          }} />
        </div>
      </header>

      {/* Main workspace */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas Area */}
        <div 
          ref={canvasRef}
          className={`flex-1 relative overflow-hidden bg-slate-100 ${
            tool === 'paste' ? 'cursor-copy' : tool === 'add' ? 'crosshair' : tool === 'defineView' ? 'crosshair' : draggingMarker ? 'grabbing' : draggingSuggestion ? 'grabbing' : editingManualView ? 'grabbing' : isPanning ? 'grabbing' : ''
          }`}
          onClick={handleCanvasClick}
          onMouseDown={handleMouseDown}
          onMouseMove={(e) => {
            handleMouseMove(e);
            handleMarkerDrag(e);
            handleManualViewMouseMove(e);
            handleSuggestionDrag(e);
          }}
          onMouseUp={(e) => {
            handleMouseUp();
            handleMarkerDragEnd();
            handleManualViewMouseUp();
            handleSuggestionDragEnd();
          }}
          onMouseLeave={(e) => {
            handleMouseUp();
            handleMarkerDragEnd();
            handleManualViewMouseUp();
            handleSuggestionDragEnd();
          }}
          onWheel={handleWheel}
          data-testid="canvas-area"
        >
          {/* Loading Overlay for Tile Loading */}
          {loadingTile && (
            <div className="absolute inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center">
              <div className="bg-white rounded-2xl shadow-2xl p-6 flex flex-col items-center gap-4 max-w-sm mx-4">
                <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
                <div className="text-center">
                  <p className="font-semibold text-slate-800 text-lg">Caricamento sezione...</p>
                  <p className="text-sm text-slate-500 mt-1">Attendere il caricamento della sezione ad alta risoluzione</p>
                </div>
              </div>
            </div>
          )}
          
          {project?.drawing_base64 ? (
            <div
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${imageRotation}deg)`,
                transformOrigin: '0 0',
                transition: isPanning ? 'none' : 'transform 0.1s ease-out'
              }}
              className="inline-block relative"
              onMouseMove={handleDrawingMouseMove}
              onMouseLeave={handleDrawingMouseLeave}
            >
              {/* Show tile image if loaded, otherwise show overview */}
              <img
                ref={imageRef}
                src={activeTileImage || project.drawing_base64}
                alt={activeTileIndex ? `Tile ${activeTileIndex}` : "Drawing"}
                className="max-w-none block"
                style={{ pointerEvents: 'none' }}
              />

              {/* Markers - positioned relative to image, draggable with select tool */}
              {markers.map((marker) => (
                <div
                  key={marker.id}
                  className={`marker-balloon ${
                    selectedMarker?.id === marker.id ? 'selected' : ''
                  } ${marker.is_ai_detected ? 'ai-detected' : ''} ${
                    draggingMarker === marker.id ? 'dragging' : ''
                  } ${tool === 'select' ? 'cursor-move' : ''}`}
                  style={{
                    position: 'absolute',
                    left: `${marker.x * 100}%`,
                    top: `${marker.y * 100}%`,
                    transform: `translate(-50%, -50%) rotate(-${imageRotation}deg)`,
                    pointerEvents: 'auto'
                  }}
                  onClick={(e) => handleMarkerClick(e, marker)}
                  onMouseDown={(e) => handleMarkerMouseDown(e, marker)}
                  data-testid={`marker-${marker.number}`}
                >
                  {marker.number}
                </div>
              ))}

              {/* AI Suggestions - smaller boxes with drag support and visual tooltip */}
              {aiSuggestions.map((suggestion) => (
                <div
                  key={suggestion.id}
                  className={`absolute w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all duration-300 ${
                    highlightedMarkerId === suggestion.id 
                      ? 'ring-4 ring-green-500 bg-green-500 text-white scale-150 z-50' 
                      : selectedSuggestion?.id === suggestion.id
                        ? 'ring-2 ring-blue-500 bg-blue-500 text-white z-50 scale-125'
                        : draggingSuggestion?.id === suggestion.id
                          ? 'ring-2 ring-blue-500 bg-blue-500 text-white z-50'
                          : 'ai-detection-box'
                  }`}
                  style={{
                    left: `${suggestion.x * 100}%`,
                    top: `${suggestion.y * 100}%`,
                    transform: highlightedMarkerId === suggestion.id 
                      ? `translate(-50%, -50%) scale(1.5) rotate(-${imageRotation}deg)` 
                      : selectedSuggestion?.id === suggestion.id
                        ? `translate(-50%, -50%) scale(1.25) rotate(-${imageRotation}deg)`
                        : `translate(-50%, -50%) rotate(-${imageRotation}deg)`,
                    animation: highlightedMarkerId === suggestion.id ? 'marker-pulse 0.8s ease-in-out infinite' : 'none',
                    boxShadow: highlightedMarkerId === suggestion.id ? '0 0 20px rgba(34, 197, 94, 0.8)' : selectedSuggestion?.id === suggestion.id ? '0 0 15px rgba(59, 130, 246, 0.6)' : 'none',
                    pointerEvents: 'auto',
                    cursor: draggingSuggestion?.id === suggestion.id ? 'grabbing' : 'pointer'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Toggle selection
                    if (selectedSuggestion?.id === suggestion.id) {
                      setSelectedSuggestion(null);
                    } else {
                      setSelectedSuggestion(suggestion);
                      setSelectedMarker(null); // Deselect any regular marker
                      setActiveTab('properties'); // Switch to properties tab to show details
                    }
                  }}
                  onMouseDown={(e) => handleSuggestionMouseDown(e, suggestion)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    acceptAISuggestion(suggestion);
                  }}
                  onMouseEnter={() => setHoveredSuggestion(suggestion)}
                  onMouseLeave={() => setHoveredSuggestion(null)}
                  onTouchStart={(e) => {
                    // On touch, show tooltip for 2 seconds then hide
                    // Long press to show, tap to accept
                    setHoveredSuggestion(suggestion);
                  }}
                  onTouchEnd={() => {
                    // Keep tooltip visible briefly after touch
                    setTimeout(() => setHoveredSuggestion(null), 2000);
                  }}
                >
                  {highlightedMarkerId === suggestion.id ? '!' : selectedSuggestion?.id === suggestion.id ? '✓' : '?'}
                  
                  {/* Visual tooltip on hover - counter-rotate to stay upright */}
                  {hoveredSuggestion?.id === suggestion.id && !draggingSuggestion && (
                    <div 
                      className="absolute bottom-full left-1/2 mb-2 px-2 py-1 bg-slate-900 text-white text-xs rounded shadow-lg whitespace-nowrap z-[100]"
                      style={{ 
                        pointerEvents: 'none',
                        transform: `translateX(-50%) rotate(-${imageRotation}deg)`
                      }}
                    >
                      <div className="font-bold">
                        {suggestion.nominal_str || suggestion.nominal || '?'}
                        {suggestion.tol_plus !== null && suggestion.tol_minus !== null && (
                          <span className="font-normal ml-1">
                            (+{suggestion.tol_plus}/{suggestion.tol_minus})
                          </span>
                        )}
                      </div>
                      {suggestion.description && suggestion.description !== String(suggestion.nominal) && suggestion.description !== suggestion.nominal_str && (
                        <div className="text-slate-300">{suggestion.description}</div>
                      )}
                      <div className="text-slate-400 text-[10px] mt-1">
                        {selectedSuggestion?.id === suggestion.id ? (
                          <span className="text-blue-400">Selezionato • Vedi proprietà a destra • CANC per eliminare</span>
                        ) : (
                          <>
                            <span className="text-yellow-400">Click</span> per dettagli • <span className="text-green-400">Trascina</span> per spostare • <span className="text-blue-400">Click DX</span> per accettare
                          </>
                        )}
                      </div>
                      {/* Arrow */}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
                    </div>
                  )}
                </div>
              ))}

              {/* Loading overlay when reading dimension with Marker AI */}
              {readingDimension && (
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center z-50 pointer-events-none">
                  <div className="bg-white rounded-lg shadow-xl p-6 flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                    <span className="text-sm font-medium text-slate-700">Lettura quota in corso...</span>
                    <span className="text-xs text-slate-500">Attendere prego</span>
                  </div>
                </div>
              )}

              {/* Detected Views Overlay - clickable rectangles */}
              {detectedViews.map((view, index) => (
                <div
                  key={`view-${index}`}
                  className={`absolute border-2 transition-all duration-200 cursor-pointer ${
                    scannedViews.includes(index)
                      ? 'border-green-500 bg-green-500/10'
                      : scanningView === index
                        ? 'border-yellow-500 bg-yellow-500/20 animate-pulse'
                        : hoveredView === index
                          ? 'border-blue-500 bg-blue-500/20'
                          : 'border-blue-400 bg-blue-400/10 hover:bg-blue-500/20'
                  }`}
                  style={{
                    left: `${view.x1}%`,
                    top: `${view.y1}%`,
                    width: `${view.x2 - view.x1}%`,
                    height: `${view.y2 - view.y1}%`,
                    pointerEvents: 'auto'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!scannedViews.includes(index) && scanningView === null) {
                      handleScanSingleView(index);
                    }
                  }}
                  onMouseEnter={() => setHoveredView(index)}
                  onMouseLeave={() => setHoveredView(null)}
                  data-testid={`view-rect-${index}`}
                >
                  {/* View Label */}
                  <div className={`absolute top-1 left-1 px-2 py-0.5 rounded text-xs font-medium ${
                    scannedViews.includes(index)
                      ? 'bg-green-500 text-white'
                      : scanningView === index
                        ? 'bg-yellow-500 text-white'
                        : 'bg-blue-500 text-white'
                  }`}>
                    {scanningView === index ? (
                      <span className="flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Scansione...
                      </span>
                    ) : scannedViews.includes(index) ? (
                      <span className="flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        {view.name || `Vista ${index + 1}`}
                      </span>
                    ) : (
                      <span>{view.name || `Vista ${index + 1}`}</span>
                    )}
                  </div>
                  
                  {/* Click to scan hint */}
                  {!scannedViews.includes(index) && scanningView !== index && hoveredView === index && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
                        Click per scansionare
                      </span>
                    </div>
                  )}
                </div>
              ))}

              {/* Manual Views Overlay - draggable/resizable rectangles */}
              {manualViews.map((view) => (
                <div
                  key={`manual-view-${view.id}`}
                  className={`absolute border-2 border-dashed transition-colors duration-150 ${
                    view.scanned
                      ? 'border-green-500 bg-green-500/10'
                      : scanningManualView === view.id
                        ? 'border-yellow-500 bg-yellow-500/20 animate-pulse'
                        : editingManualView === view.id
                          ? 'border-purple-600 bg-purple-500/20'
                          : 'border-purple-400 bg-purple-400/10 hover:bg-purple-500/20'
                  }`}
                  style={{
                    left: `${view.x1}%`,
                    top: `${view.y1}%`,
                    width: `${view.x2 - view.x1}%`,
                    height: `${view.y2 - view.y1}%`,
                    pointerEvents: view.scanned ? 'none' : 'auto',
                    cursor: view.scanned ? 'default' : (tool === 'defineView' || tool === 'select' ? 'move' : 'pointer'),
                    touchAction: view.scanned ? 'auto' : 'none'  // Enable touch dragging
                  }}
                  onMouseDown={(e) => !view.scanned && (tool === 'defineView' || tool === 'select') && handleManualViewMouseDown(e, view.id, null)}
                  onTouchStart={(e) => !view.scanned && (tool === 'defineView' || tool === 'select') && handleManualViewTouchStart(e, view.id, null)}
                  onTouchMove={handleManualViewTouchMove}
                  onTouchEnd={handleManualViewTouchEnd}
                  data-testid={`manual-view-${view.id}`}
                >
                  {/* View Label */}
                  <div className={`absolute top-1 left-1 px-2 py-0.5 rounded text-xs font-medium ${
                    view.scanned
                      ? 'bg-green-500 text-white'
                      : scanningManualView === view.id
                        ? 'bg-yellow-500 text-white'
                        : 'bg-purple-500 text-white'
                  }`}>
                    {scanningManualView === view.id ? (
                      <span className="flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Scansione...
                      </span>
                    ) : view.scanned ? (
                      <span className="flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        {view.name}
                      </span>
                    ) : (
                      <span>{view.name}</span>
                    )}
                  </div>
                  
                  {/* Delete button */}
                  {!view.scanned && (
                    <button
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteManualView(view.id);
                      }}
                      title="Elimina vista"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                  
                  {/* Resize handles - only show when in defineView mode and not scanned */}
                  {(tool === 'defineView' || tool === 'select') && !view.scanned && (
                    <>
                      {/* NW corner */}
                      <div
                        className="absolute -top-2 -left-2 w-5 h-5 bg-purple-600 border-2 border-white rounded-sm cursor-nw-resize"
                        style={{ touchAction: 'none' }}
                        onMouseDown={(e) => handleManualViewMouseDown(e, view.id, 'nw')}
                        onTouchStart={(e) => handleManualViewTouchStart(e, view.id, 'nw')}
                        onTouchMove={handleManualViewTouchMove}
                        onTouchEnd={handleManualViewTouchEnd}
                      />
                      {/* NE corner */}
                      <div
                        className="absolute -top-2 -right-2 w-5 h-5 bg-purple-600 border-2 border-white rounded-sm cursor-ne-resize"
                        style={{ touchAction: 'none' }}
                        onMouseDown={(e) => handleManualViewMouseDown(e, view.id, 'ne')}
                        onTouchStart={(e) => handleManualViewTouchStart(e, view.id, 'ne')}
                        onTouchMove={handleManualViewTouchMove}
                        onTouchEnd={handleManualViewTouchEnd}
                      />
                      {/* SW corner */}
                      <div
                        className="absolute -bottom-2 -left-2 w-5 h-5 bg-purple-600 border-2 border-white rounded-sm cursor-sw-resize"
                        style={{ touchAction: 'none' }}
                        onMouseDown={(e) => handleManualViewMouseDown(e, view.id, 'sw')}
                        onTouchStart={(e) => handleManualViewTouchStart(e, view.id, 'sw')}
                        onTouchMove={handleManualViewTouchMove}
                        onTouchEnd={handleManualViewTouchEnd}
                      />
                      {/* SE corner */}
                      <div
                        className="absolute -bottom-2 -right-2 w-5 h-5 bg-purple-600 border-2 border-white rounded-sm cursor-se-resize"
                        style={{ touchAction: 'none' }}
                        onMouseDown={(e) => handleManualViewMouseDown(e, view.id, 'se')}
                        onTouchStart={(e) => handleManualViewTouchStart(e, view.id, 'se')}
                        onTouchMove={handleManualViewTouchMove}
                        onTouchEnd={handleManualViewTouchEnd}
                      />
                    </>
                  )}
                  
                  {/* Scan button - shown in center when not scanned */}
                  {!view.scanned && scanningManualView !== view.id && tool !== 'defineView' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <button
                        className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleScanManualView(view.id);
                        }}
                      >
                        Scansiona
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                {...getDrawingRootProps()}
                className="border-2 border-dashed border-slate-300 rounded-lg p-12 text-center cursor-pointer hover:border-primary transition-colors"
                data-testid="upload-drawing-area"
              >
                <input {...getDrawingInputProps()} />
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 font-medium">{t('drop_file')}</p>
                <p className="text-slate-400 text-sm mt-2">{t('supported_formats')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Real-time dimension preview tooltip - DISABLED */}

        {/* Right Sidebar */}
        <div className="w-72 border-l border-slate-200 bg-white flex flex-col">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
            <TabsList className="w-full justify-start px-2 pt-2 bg-transparent">
              <TabsTrigger value="properties" className="text-xs">Properties</TabsTrigger>
              <TabsTrigger value="upload" className="text-xs">Upload</TabsTrigger>
              {(aiSuggestions.length > 0 || aiDetecting || detectedViews.length > 0 || detectingViews || manualViews.length > 0 || tool === 'defineView') && (
                <TabsTrigger value="ai" className="text-xs text-amber-600">
                  {aiDetecting || detectingViews ? 'AI...' : 
                   manualViews.length > 0 && detectedViews.length === 0 && aiSuggestions.length === 0 
                     ? `Viste (${manualViews.length})`
                     : `AI (${aiSuggestions.length})`}
                </TabsTrigger>
              )}
              {/* Tiles tab for large drawings */}
              {isTiled && tiles.length > 0 && (
                <TabsTrigger value="tiles" className="text-xs text-blue-600">
                  Sezioni ({tiles.length})
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="properties" className="flex-1 p-4 mt-0">
              {selectedMarker ? (
                <ScrollArea className="h-full">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-900">{t('marker')} #</span>
                        <MarkerNumberInput 
                          marker={selectedMarker} 
                          updateMarker={updateMarker}
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Copy button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                          onClick={copyMarker}
                          title="Copia marker (Ctrl+C)"
                          data-testid="copy-marker-btn"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        {/* Paste button - always visible, disabled if nothing copied */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className={copiedMarker 
                            ? "text-green-500 hover:text-green-700 hover:bg-green-50" 
                            : "text-slate-300 cursor-not-allowed"
                          }
                          onClick={pasteMarker}
                          disabled={!copiedMarker}
                          title={copiedMarker ? "Incolla marker (Ctrl+V)" : "Prima copia un marker con Ctrl+C"}
                          data-testid="paste-marker-btn"
                        >
                          <ClipboardPaste className="w-4 h-4" />
                        </Button>
                        {/* Delete button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={deleteMarker}
                          data-testid="delete-marker-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <Label className="dense-label">{t('description')}</Label>
                        <Input
                          value={selectedMarker.description || ''}
                          onChange={(e) => updateMarker('description', e.target.value)}
                          className="dense-input"
                          data-testid="marker-description"
                        />
                      </div>
                      <div>
                        <Label className="dense-label">{t('nominal')}</Label>
                        <NominalInput 
                          marker={selectedMarker} 
                          updateMarker={updateMarker}
                          t={t}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="dense-label">{t('tolerance_plus')}</Label>
                          <TolPlusInput 
                            marker={selectedMarker} 
                            updateMarker={updateMarker}
                          />
                        </div>
                        <div>
                          <Label className="dense-label">{t('tolerance_minus')}</Label>
                          <TolMinusInput 
                            marker={selectedMarker} 
                            updateMarker={updateMarker}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="dense-label">{t('measured_value')}</Label>
                        <Input
                          value={selectedMarker.measured ?? ''}
                          onChange={(e) => updateMarker('measured', e.target.value || null)}
                          placeholder="Value or OK"
                          className="dense-input font-mono"
                          data-testid="marker-measured"
                        />
                      </div>
                      <div>
                        <Label className="dense-label">{t('instrument')}</Label>
                        <InstrumentAutocomplete
                          marker={selectedMarker}
                          markers={markers}
                          updateMarker={updateMarker}
                        />
                      </div>
                      <div>
                        <Label className="dense-label">{t('resolution')}</Label>
                        <Input
                          value={selectedMarker.resolution || ''}
                          onChange={(e) => updateMarker('resolution', e.target.value)}
                          className="dense-input"
                          data-testid="marker-resolution"
                          placeholder="es. 0.01, 0.001"
                        />
                      </div>

                      {/* Result display */}
                      <div className="pt-2 border-t border-slate-200">
                        <Label className="dense-label">{t('result')}</Label>
                        <div className={`mt-1 px-3 py-2 rounded text-sm font-bold text-center ${
                          selectedMarker.is_ok === true 
                            ? 'bg-green-100 text-green-700' 
                            : selectedMarker.is_ok === false 
                              ? 'bg-red-100 text-red-700'
                              : 'bg-slate-100 text-slate-500'
                        }`}>
                          {selectedMarker.is_ok === true ? t('ok') : 
                           selectedMarker.is_ok === false ? t('not_ok') : 'N/A'}
                        </div>
                      </div>
                      
                      {/* Add another marker button */}
                      <div className="pt-4 border-t border-slate-200 space-y-2">
                        {/* Save Button - now saves both markers and AI suggestions */}
                        <Button
                          onClick={handleSaveAll}
                          disabled={(!hasUnsavedChanges && !hasUnsavedSuggestions) || savingMarkers}
                          className={`w-full ${(hasUnsavedChanges || hasUnsavedSuggestions) ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-400'}`}
                          data-testid="save-markers-btn"
                        >
                          {savingMarkers ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          {(hasUnsavedChanges || hasUnsavedSuggestions) ? (
                            <>
                              Salva Tutto
                              {aiSuggestions.length > 0 && (
                                <span className="ml-1 bg-white/20 px-1.5 rounded text-xs">+{aiSuggestions.length} AI</span>
                              )}
                            </>
                          ) : 'Salvato'}
                        </Button>
                        
                        <Button
                          onClick={handleAddAnother}
                          variant="outline"
                          className="w-full"
                          data-testid="add-another-btn"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Aggiungi altra quota
                        </Button>
                        <p className="text-xs text-slate-400 mt-2 text-center">
                          I campi vuoti verranno ignorati nell'export
                        </p>
                      </div>
                    </div>
                  </div>
                </ScrollArea>
              ) : selectedSuggestion ? (
                /* AI Suggestion Properties Panel */
                <ScrollArea className="h-full">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-amber-600">Suggerimento AI</span>
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Da confermare</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setAiSuggestions(prev => prev.filter(s => s.id !== selectedSuggestion.id));
                          setSelectedSuggestion(null);
                          toast.success('Suggerimento rimosso');
                        }}
                        title="Elimina suggerimento (CANC)"
                        data-testid="delete-suggestion-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="space-y-3 bg-amber-50 p-3 rounded-lg border border-amber-200">
                      <div>
                        <Label className="dense-label text-amber-700">Valore Nominale</Label>
                        <div className="font-mono text-lg font-bold text-amber-900">
                          {selectedSuggestion.nominal_str || selectedSuggestion.nominal || '—'}
                        </div>
                      </div>
                      
                      {(selectedSuggestion.tol_plus !== null || selectedSuggestion.tol_minus !== null) && (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="dense-label text-amber-700">Toll. Sup.</Label>
                            <div className="font-mono text-sm text-amber-800">
                              {selectedSuggestion.tol_plus !== null ? `+${selectedSuggestion.tol_plus}` : '—'}
                            </div>
                          </div>
                          <div>
                            <Label className="dense-label text-amber-700">Toll. Inf.</Label>
                            <div className="font-mono text-sm text-amber-800">
                              {selectedSuggestion.tol_minus !== null ? selectedSuggestion.tol_minus : '—'}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {selectedSuggestion.description && (
                        <div>
                          <Label className="dense-label text-amber-700">Descrizione</Label>
                          <div className="text-sm text-amber-800">{selectedSuggestion.description}</div>
                        </div>
                      )}
                      
                      {/* Strumento e Risoluzione se presenti */}
                      {(selectedSuggestion.instrument || selectedSuggestion.resolution) && (
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-amber-200">
                          <div>
                            <Label className="dense-label text-amber-700">Strumento</Label>
                            <div className="text-sm text-amber-800">{selectedSuggestion.instrument || '—'}</div>
                          </div>
                          <div>
                            <Label className="dense-label text-amber-700">Risoluzione</Label>
                            <div className="text-sm text-amber-800">{selectedSuggestion.resolution || '—'}</div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                      <strong>Suggerimento:</strong> Puoi trascinare il marker sul disegno per riposizionarlo, oppure cliccare "Modifica" per cambiare i valori prima di confermare.
                    </div>

                    <div className="pt-4 border-t border-slate-200 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          onClick={() => setEditingSuggestion(selectedSuggestion)}
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          Modifica
                        </Button>
                        <Button
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => {
                            acceptAISuggestion(selectedSuggestion);
                            setSelectedSuggestion(null);
                          }}
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Accetta
                        </Button>
                      </div>
                      <Button
                        variant="outline"
                        className="w-full text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => {
                          setAiSuggestions(prev => prev.filter(s => s.id !== selectedSuggestion.id));
                          setSelectedSuggestion(null);
                          toast.success('Suggerimento rimosso');
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Rifiuta
                      </Button>
                      <p className="text-xs text-slate-400 mt-2 text-center">
                        Premi CANC per eliminare rapidamente
                      </p>
                    </div>
                  </div>
                </ScrollArea>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 text-sm text-center p-4">
                  {readingDimension ? (
                    <>
                      <Loader2 className="w-8 h-8 animate-spin text-primary mb-2" />
                      <p>Lettura quota in corso...</p>
                    </>
                  ) : (
                    <>
                      <p className="mb-4">{t('select_marker_or_add_new')}</p>
                      <Button onClick={() => setTool('add')} variant="outline" data-testid="start-add-marker">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('add_marker')}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="upload" className="flex-1 p-4 mt-0">
              <div className="space-y-4">
                <div>
                  <Label className="dense-label">{t('upload_drawing')}</Label>
                  <div
                    {...getDrawingRootProps()}
                    className={`mt-2 border-2 border-dashed rounded p-4 text-center transition-colors relative ${
                      uploadingDrawing 
                        ? 'border-blue-400 bg-blue-50 cursor-wait' 
                        : 'border-slate-200 cursor-pointer hover:border-primary'
                    }`}
                    data-testid="upload-drawing-sidebar"
                  >
                    <input {...getDrawingInputProps()} disabled={uploadingDrawing} />
                    {uploadingDrawing ? (
                      <>
                        <Loader2 className="w-8 h-8 text-blue-500 mx-auto mb-2 animate-spin" />
                        <p className="text-sm text-blue-600 font-medium">Caricamento in corso...</p>
                        <p className="text-xs text-blue-500 mt-1">Elaborazione disegno, attendere</p>
                      </>
                    ) : (
                      <>
                        <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                        <p className="text-xs text-slate-500">{t('drop_file')}</p>
                      </>
                    )}
                  </div>
                  {project?.drawing_filename && !uploadingDrawing && (
                    <p className="text-xs text-slate-500 mt-2 truncate">
                      Current: {project.drawing_filename}
                    </p>
                  )}
                </div>

                <Separator />
                
                {/* CMM Report Upload - MULTIPLE (up to 5) */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="dense-label">Report CMM (PDF) - Max 5</Label>
                    <button
                      onClick={() => setShowCMMHelp(!showCMMHelp)}
                      className="text-blue-500 hover:text-blue-700 p-1 rounded-full hover:bg-blue-50"
                      title="Come preparare il report CMM"
                    >
                      <HelpCircle className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* CMM Format Instructions */}
                  {showCMMHelp && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs">
                      <p className="font-bold text-blue-800 mb-2">Come preparare il report CMM</p>
                      
                      <p className="text-blue-700 mb-2">
                        Per abilitare l'importazione automatica, il programma CMM deve includere 
                        un <strong>identificatore marker</strong> nella descrizione di ogni misura.
                      </p>
                      
                      <div className="bg-white rounded p-2 mb-2">
                        <p className="font-semibold text-slate-700 mb-1">Formato:</p>
                        <code className="text-blue-600 font-mono">SB123</code> oppure <code className="text-blue-600 font-mono">sb123</code>
                        <p className="text-slate-500 mt-1">dove 123 = numero del marker in SmartBalloon</p>
                      </div>
                      
                      <div className="bg-white rounded p-2 mb-2">
                        <p className="font-semibold text-slate-700 mb-1">Esempi corretti:</p>
                        <ul className="text-slate-600 space-y-0.5">
                          <li>• "Diametro D=4 <strong className="text-green-600">SB131</strong>"</li>
                          <li>• "Posizione X <strong className="text-green-600">SB45</strong>"</li>
                          <li>• "Centro foro <strong className="text-green-600">SB7</strong>"</li>
                        </ul>
                      </div>
                      
                      <div className="text-blue-700">
                        <p className="font-semibold mb-1">Note:</p>
                        <ul className="space-y-0.5">
                          <li>• L'identificatore deve essere nella descrizione</li>
                          <li>• Deve corrispondere al numero del marker</li>
                          <li>• Maiuscole/minuscole non importano</li>
                          <li>• <strong>Puoi caricare fino a 5 report</strong></li>
                          <li>• Le quote duplicate verranno segnalate</li>
                        </ul>
                      </div>
                      
                      <div className="mt-2 pt-2 border-t border-blue-200 text-blue-700">
                        <p className="font-semibold">Risultato:</p>
                        <p>SB131 → Marker 131 ✓</p>
                        <p>SB45 → Marker 45 ✓</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Dropzone for multiple CMM reports */}
                  <div
                    {...getCMMMultiRootProps()}
                    className={`mt-2 border-2 border-dashed rounded p-4 text-center transition-colors ${
                      cmmReports.length >= 5 
                        ? 'border-slate-300 bg-slate-50 cursor-not-allowed'
                        : uploadingCMMReports
                          ? 'border-blue-400 bg-blue-50 cursor-wait'
                          : 'border-slate-200 cursor-pointer hover:border-blue-400'
                    }`}
                    data-testid="upload-cmm-reports-multiple"
                  >
                    <input {...getCMMMultiInputProps()} />
                    {uploadingCMMReports ? (
                      <>
                        <Loader2 className="w-6 h-6 text-blue-500 mx-auto mb-2 animate-spin" />
                        <p className="text-xs text-blue-600">Caricamento...</p>
                      </>
                    ) : cmmReports.length >= 5 ? (
                      <>
                        <FileText className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                        <p className="text-xs text-slate-500">{t('limit_reached')} (5/5)</p>
                      </>
                    ) : (
                      <>
                        <FileText className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                        <p className="text-xs text-slate-500">
                          {t('drag_cmm_report')} ({cmmReports.length}/5)
                        </p>
                      </>
                    )}
                  </div>
                  
                  {/* List of uploaded CMM reports */}
                  {cmmReports.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {cmmReports.map((report) => (
                        <div 
                          key={report.id} 
                          className="p-2 bg-blue-50 rounded flex items-center justify-between group"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            <span className="text-xs text-blue-700 truncate" title={report.filename}>
                              {report.filename}
                            </span>
                          </div>
                          <button
                            onClick={() => handleDeleteSingleCMMReport(report.id)}
                            className="text-red-500 hover:text-red-700 p-1 opacity-50 group-hover:opacity-100 transition-opacity"
                            title={t('delete')}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Import button for multiple reports */}
                  {cmmReports.length > 0 && (
                    <Button
                      onClick={handleParseCMMReportsMultiple}
                      disabled={parsingCMMReport}
                      variant="outline"
                      className="w-full mt-2 border-blue-500 text-blue-600 hover:bg-blue-50"
                      data-testid="import-cmm-multiple-btn"
                    >
                      {parsingCMMReport ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      {t('import_from_reports').replace('{count}', cmmReports.length)}
                    </Button>
                  )}
                  
                  {/* Legacy single report (if exists) */}
                  {cmmReportStatus?.has_report && cmmReports.length === 0 && (
                    <>
                      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-amber-600 flex-shrink-0" />
                          <span className="text-xs text-amber-700 truncate">
                            {cmmReportStatus.filename} (legacy)
                          </span>
                        </div>
                        <button
                          onClick={handleDeleteCMMReport}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Rimuovi report"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <Button
                        onClick={handleParseCMMReport}
                        disabled={parsingCMMReport}
                        variant="outline"
                        className="w-full mt-2 border-amber-500 text-amber-600 hover:bg-amber-50"
                        data-testid="import-cmm-btn"
                      >
                        {parsingCMMReport ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Importa (legacy)
                      </Button>
                    </>
                  )}
                </div>

                <Separator />

                {/* General Tolerances Analysis */}
                <div>
                  <Label className="dense-label">Tolleranze Generali</Label>
                  <Button
                    onClick={handleAnalyzeTolerances}
                    disabled={analyzingTolerances || !project?.drawing_base64}
                    variant="outline"
                    className="w-full mt-2"
                    data-testid="analyze-tolerances-btn"
                  >
                    {analyzingTolerances ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Settings2 className="w-4 h-4 mr-2" />
                    )}
                    {t('analyze_titleblock')}
                  </Button>
                  {generalTolerances && (
                    <div className="mt-2 p-2 bg-slate-50 rounded text-xs">
                      <p className="font-bold text-slate-700">
                        {generalTolerances.tolerance_class || 'ISO 2768-m'}
                      </p>
                      <p className="text-slate-500 mt-1">
                        {t('tolerances_auto_apply')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {(aiSuggestions.length > 0 || aiDetecting || detectedViews.length > 0 || detectingViews || manualViews.length > 0 || tool === 'defineView') && (
              <TabsContent value="ai" className="flex-1 p-4 mt-0 flex flex-col overflow-hidden">
                <div className="flex flex-col h-full">
                  {/* Manual Views Section - Collapsible when suggestions exist */}
                  {(manualViews.length > 0 || tool === 'defineView') && (
                    <div className={`space-y-2 ${aiSuggestions.length > 0 ? 'pb-3 border-b border-slate-200' : ''}`}>
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-purple-700 flex items-center gap-2">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" strokeDasharray="4 2" />
                          </svg>
                          Viste Manuali ({manualViews.filter(v => v.scanned).length}/{manualViews.length})
                        </h3>
                        <div className="flex gap-1">
                          {manualViews.length > 0 && manualViews.some(v => !v.scanned) && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={handleScanAllManualViews}
                              disabled={scanningManualView !== null}
                              className="text-xs border-purple-500 text-purple-600 hover:bg-purple-50"
                            >
                              <Sparkles className="w-3 h-3 mr-1" />
                              Scansiona Tutte
                            </Button>
                          )}
                          {manualViews.length > 0 && (
                            <Button size="sm" variant="ghost" onClick={clearManualViews}>
                              <X className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Instructions */}
                      {tool === 'defineView' && manualViews.length === 0 && (
                        <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-700">
                          <p className="font-medium mb-1">Come usare:</p>
                          <ol className="list-decimal list-inside space-y-1 text-xs">
                            <li>Clicca sul disegno per creare una vista</li>
                            <li>Trascina i bordi viola per ridimensionare</li>
                            <li>Trascina il centro per spostare</li>
                            <li>Clicca "Scansiona Tutte" quando hai finito</li>
                          </ol>
                        </div>
                      )}
                      
                      {/* Scan Options */}
                      {manualViews.length > 0 && manualViews.some(v => !v.scanned) && (
                        <div className="flex items-center gap-2 p-2 bg-slate-50 rounded text-xs">
                          <input
                            type="checkbox"
                            id="scanRotated"
                            checked={scanRotated}
                            onChange={(e) => setScanRotated(e.target.checked)}
                            className="w-4 h-4 text-purple-600 rounded"
                          />
                          <label htmlFor="scanRotated" className="text-slate-600">
                            Scansiona anche quote ruotate (90°)
                          </label>
                          <span className="text-slate-400 ml-auto">
                            {scanRotated ? '~2 crediti' : '~1 credito'}/vista
                          </span>
                        </div>
                      )}
                      
                      {/* Progress Bar */}
                      {manualViews.length > 0 && (
                        <div className="space-y-1">
                          <Progress 
                            value={(manualViews.filter(v => v.scanned).length / manualViews.length) * 100} 
                            className="h-2 bg-purple-100"
                          />
                          <p className="text-xs text-slate-500">
                            {manualViews.every(v => v.scanned)
                              ? '✅ Tutte le viste manuali scansionate!' 
                              : `${manualViews.filter(v => !v.scanned).length} viste da scansionare (~2 crediti ciascuna)`}
                          </p>
                        </div>
                      )}
                      
                      {/* Manual View List - Compact when suggestions exist */}
                      {manualViews.length > 0 && (
                        <div className={`space-y-1 overflow-y-auto ${aiSuggestions.length > 0 ? 'max-h-20' : 'max-h-32'}`}>
                          {manualViews.map((view) => (
                            <div 
                              key={view.id}
                              className={`flex items-center justify-between p-2 rounded text-sm ${
                                view.scanned 
                                  ? 'bg-green-50 text-green-700' 
                                  : scanningManualView === view.id
                                    ? 'bg-yellow-50 text-yellow-700'
                                    : 'bg-purple-50 hover:bg-purple-100'
                              }`}
                            >
                              <span className="flex items-center gap-2 flex-1 min-w-0">
                                {view.scanned ? (
                                  <Check className="w-3 h-3 text-green-600 flex-shrink-0" />
                                ) : scanningManualView === view.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
                                ) : (
                                  <svg className="w-3 h-3 text-purple-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="18" height="18" rx="2" strokeDasharray="4 2" />
                                  </svg>
                                )}
                                {editingViewName === view.id ? (
                                  <Input
                                    autoFocus
                                    defaultValue={view.name}
                                    className="h-6 text-xs px-1 py-0"
                                    onBlur={(e) => handleRenameView(view.id, e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleRenameView(view.id, e.target.value);
                                      } else if (e.key === 'Escape') {
                                        setEditingViewName(null);
                                      }
                                    }}
                                  />
                                ) : (
                                  <span 
                                    className="truncate cursor-pointer hover:underline" 
                                    onDoubleClick={() => setEditingViewName(view.id)}
                                    title="Doppio click per rinominare"
                                  >
                                    {view.name}
                                  </span>
                                )}
                              </span>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {!view.scanned && scanningManualView !== view.id && (
                                  <>
                                    <button
                                      className="text-xs text-slate-500 hover:text-purple-800 px-1"
                                      onClick={() => setEditingViewName(view.id)}
                                      title="Rinomina"
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                    <button
                                      className="text-xs text-purple-600 hover:text-purple-800 px-2 py-0.5 rounded hover:bg-purple-100"
                                      onClick={() => handleScanManualView(view.id)}
                                    >
                                      Scansiona
                                    </button>
                                    <button
                                      className="text-xs text-red-500 hover:text-red-700 px-1"
                                      onClick={() => deleteManualView(view.id)}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {manualViews.length > 0 && (detectedViews.length > 0 || aiSuggestions.length > 0) && <Separator />}

                  {/* View-by-View Mode */}
                  {(detectedViews.length > 0 || detectingViews) && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-blue-700 flex items-center gap-2">
                          <CircleIcon className="w-4 h-4" />
                          {t('detected_views')} ({scannedViews.length}/{detectedViews.length})
                        </h3>
                        <div className="flex gap-1">
                          {detectedViews.length > 0 && scannedViews.length < detectedViews.length && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={handleScanAllViews}
                              disabled={scanningView !== null}
                              className="text-xs"
                            >
                              <Sparkles className="w-3 h-3 mr-1" />
                              {t('scan_all')}
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={clearDetectedViews}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      
                      {detectingViews && (
                        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                          <span className="text-blue-700 text-sm">{t('detecting_views')}</span>
                        </div>
                      )}
                      
                      {/* Progress Bar */}
                      {detectedViews.length > 0 && (
                        <div className="space-y-1">
                          <Progress value={(scannedViews.length / detectedViews.length) * 100} className="h-2" />
                          <p className="text-xs text-slate-500">
                            {scannedViews.length === detectedViews.length 
                              ? `✅ ${t('all_views_scanned')}` 
                              : t('click_view_to_scan')}
                          </p>
                        </div>
                      )}
                      
                      {/* View List */}
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {detectedViews.map((view, index) => (
                          <div 
                            key={index}
                            className={`flex items-center justify-between p-2 rounded text-sm cursor-pointer transition-colors ${
                              scannedViews.includes(index) 
                                ? 'bg-green-50 text-green-700' 
                                : scanningView === index
                                  ? 'bg-yellow-50 text-yellow-700'
                                  : 'bg-slate-50 hover:bg-blue-50'
                            }`}
                            onClick={() => !scannedViews.includes(index) && scanningView === null && handleScanSingleView(index)}
                            onMouseEnter={() => setHoveredView(index)}
                            onMouseLeave={() => setHoveredView(null)}
                          >
                            <span className="flex items-center gap-2">
                              {scannedViews.includes(index) ? (
                                <Check className="w-3 h-3 text-green-600" />
                              ) : scanningView === index ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <CircleIcon className="w-3 h-3 text-slate-400" />
                              )}
                              {view.name || `Vista ${index + 1}`}
                            </span>
                            {!scannedViews.includes(index) && scanningView !== index && (
                              <span className="text-xs text-slate-400">~2 crediti</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {detectedViews.length > 0 && aiSuggestions.length > 0 && <Separator />}
                  
                  {/* Static message during detection */}
                  {aiDetecting && (
                    <div className="space-y-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
                        <div>
                          <p className="text-amber-700 font-semibold">Scansione AI in corso...</p>
                          <p className="text-amber-600 text-sm mt-1">
                            Per disegni complessi, la scansione può richiedere diverse decine di minuti.
                          </p>
                          <p className="text-amber-500 text-xs mt-2 font-medium">
                            ⚠️ Non ricaricare la pagina
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {aiSuggestions.length > 0 && (
                    <div className="flex flex-col flex-1 min-h-0 mt-3">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-amber-700 text-base">{t('ai_suggestions')} ({aiSuggestions.length})</h3>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={acceptAllSuggestions} data-testid="accept-all-ai" className="h-7">
                            <Check className="w-3 h-3 mr-1" />
                            All
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setAiSuggestions([])} data-testid="reject-all-ai" className="h-7">
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>

                      <ScrollArea className="flex-1 min-h-0" style={{ maxHeight: 'calc(100vh - 450px)', minHeight: '200px' }}>
                        <div className="space-y-2 pr-2">
                          {aiSuggestions.map((suggestion) => (
                            <div 
                              key={suggestion.id}
                              className={`p-3 border rounded-lg text-sm cursor-pointer transition-all duration-300 ${
                                highlightedMarkerId === suggestion.id 
                                  ? 'border-green-500 bg-green-50 ring-2 ring-green-300 shadow-md' 
                                  : 'border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 hover:shadow-sm'
                              }`}
                              onClick={() => zoomToMarker(suggestion)}
                            >
                              {/* Header row with value and tolerances */}
                              <div className="flex justify-between items-start gap-2">
                                <div className="font-mono text-amber-900 font-bold text-base">
                                  {suggestion.nominal_str || suggestion.nominal || suggestion.description}
                                </div>
                                {/* Tolerance display */}
                                <div className="text-xs text-right flex-shrink-0">
                                  {(suggestion.tol_plus !== null && suggestion.tol_plus !== undefined) || 
                                   (suggestion.tol_minus !== null && suggestion.tol_minus !== undefined) ? (
                                    // Check if symmetric (±X)
                                    suggestion.tol_plus === -suggestion.tol_minus ? (
                                      <span className="text-amber-700 font-medium bg-amber-100 px-1.5 py-0.5 rounded">±{Math.abs(suggestion.tol_plus)}</span>
                                    ) : (
                                      // Asymmetric tolerances
                                      <div className="flex flex-col items-end bg-slate-100 px-1.5 py-0.5 rounded">
                                        <span className="text-green-600 font-medium">
                                          {suggestion.tol_plus !== null && suggestion.tol_plus !== undefined 
                                            ? (suggestion.tol_plus >= 0 ? '+' : '') + suggestion.tol_plus 
                                            : '—'}
                                        </span>
                                        <span className="text-red-600 font-medium">
                                          {suggestion.tol_minus !== null && suggestion.tol_minus !== undefined 
                                            ? (suggestion.tol_minus >= 0 ? '+' : '') + suggestion.tol_minus 
                                            : '—'}
                                        </span>
                                      </div>
                                    )
                                  ) : (
                                    <span className="text-slate-400 italic text-xs">ISO 2768</span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Description if different */}
                              {suggestion.description && suggestion.description !== String(suggestion.nominal) && suggestion.description !== suggestion.nominal_str && (
                                <div className="text-xs text-slate-500 mt-1 truncate">{suggestion.description}</div>
                              )}
                              
                              {/* Action buttons - more compact */}
                              <div className="flex justify-between items-center mt-2 pt-2 border-t border-amber-200/50">
                                <span className="text-xs text-amber-600 flex items-center gap-1 opacity-70">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                  </svg>
                                  Zoom
                                </span>
                                <div className="flex gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="h-6 px-2 text-xs text-blue-600 border-blue-300 hover:bg-blue-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingSuggestion({...suggestion});
                                    }}
                                  >
                                    <Pencil className="w-3 h-3 mr-1" />
                                    Modify
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="default"
                                    className="h-6 px-2 text-xs bg-green-600 hover:bg-green-700 text-white"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      acceptAISuggestion(suggestion);
                                    }}
                                  >
                                    <Check className="w-3 h-3 mr-1" />
                                    Accept
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    className="h-6 px-1.5 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAiSuggestions(aiSuggestions.filter(s => s.id !== suggestion.id));
                                    }}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  )}
                </div>
              </TabsContent>
            )}
            
            {/* Tiles Tab - for large drawings split into sections */}
            {isTiled && tiles.length > 0 && (
              <TabsContent value="tiles" className="flex-1 p-4 mt-0 flex flex-col overflow-hidden">
                <div className="flex flex-col h-full">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-blue-700 flex items-center gap-2">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="7" height="7" />
                          <rect x="14" y="3" width="7" height="7" />
                          <rect x="3" y="14" width="7" height="7" />
                          <rect x="14" y="14" width="7" height="7" />
                        </svg>
                        Sezioni Disegno ({tiles.length})
                      </h3>
                    </div>
                    
                    <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                      <p className="font-medium mb-1">Disegno di grandi dimensioni</p>
                      <p className="text-xs">
                        Il disegno è stato diviso in {tiles.length} sezioni ad alta risoluzione per preservare la qualità.
                        Seleziona una sezione per lavorare su quella parte del disegno.
                      </p>
                    </div>
                    
                    {/* Overview Button */}
                    <Button
                      variant={activeTileIndex === null ? "default" : "outline"}
                      className="w-full justify-start"
                      onClick={showOverview}
                    >
                      <Maximize className="w-4 h-4 mr-2" />
                      Vista d'insieme
                      {activeTileIndex === null && <Check className="w-4 h-4 ml-auto" />}
                    </Button>
                    
                    {/* Tiles List */}
                    <ScrollArea className="flex-1 max-h-[300px]">
                      <div className="space-y-2">
                        {tiles.map((tile) => (
                          <div 
                            key={tile.index}
                            className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                              activeTileIndex === tile.index 
                                ? 'bg-blue-100 border-blue-500' 
                                : 'bg-white border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                            }`}
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {/* Tile preview grid position indicator */}
                              <div 
                                className="w-8 h-8 rounded bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600"
                                title={`Riga ${tile.row + 1}, Colonna ${tile.col + 1}`}
                              >
                                {tile.index}
                              </div>
                              
                              {/* Editable name */}
                              {editingTileName === tile.index ? (
                                <Input
                                  autoFocus
                                  defaultValue={tile.name}
                                  className="h-7 text-sm"
                                  onBlur={(e) => handleRenameTile(tile.index, e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleRenameTile(tile.index, e.target.value);
                                    } else if (e.key === 'Escape') {
                                      setEditingTileName(null);
                                    }
                                  }}
                                />
                              ) : (
                                <div 
                                  className="flex-1 min-w-0 cursor-pointer group"
                                  onDoubleClick={() => setEditingTileName(tile.index)}
                                >
                                  <p className="font-medium text-sm truncate">
                                    {tile.name}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {tile.pixel_width}×{tile.pixel_height}px
                                  </p>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => setEditingTileName(tile.index)}
                                title="Rinomina"
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button
                                variant={activeTileIndex === tile.index ? "default" : "outline"}
                                size="sm"
                                onClick={() => loadTileImage(tile.index)}
                                disabled={loadingTile}
                              >
                                {loadingTile && activeTileIndex === tile.index ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Image className="w-4 h-4 mr-1" />
                                    {activeTileIndex === tile.index ? 'Attiva' : 'Carica'}
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>

      {/* Collapsible Bottom ISIR Panel */}
      <div className={`border-t border-slate-200 bg-white flex-shrink-0 transition-all duration-300 ${showISIR ? 'h-64' : 'h-10'}`}>
        <div className="h-full flex flex-col">
          {/* Header - always visible, clickable to toggle */}
          <button
            onClick={() => setShowISIR(!showISIR)}
            className="px-4 py-2 border-b border-slate-200 flex items-center justify-between hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              {showISIR ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <h3 className="font-bold text-sm text-slate-900">ISIR Data</h3>
            </div>
            <span className="text-xs text-slate-500">
              {markers.length} {t('markers')} {!showISIR && `(${t('click_to_expand')})`}
            </span>
          </button>
          
          {/* Content - only visible when expanded */}
          {showISIR && (
            <ScrollArea className="flex-1">
              <Table className="data-table-compact">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>{t('description')}</TableHead>
                    <TableHead className="w-24 text-right">{t('nominal')}</TableHead>
                    <TableHead className="w-20 text-right">Tol+</TableHead>
                    <TableHead className="w-20 text-right">Tol-</TableHead>
                    <TableHead className="w-24 text-right">{t('measured_value')}</TableHead>
                    <TableHead>{t('instrument')}</TableHead>
                    <TableHead className="w-24">{t('resolution')}</TableHead>
                    <TableHead className="w-20 text-center">{t('result')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {markers.map((marker) => (
                    <TableRow 
                      key={marker.id}
                      className={`cursor-pointer hover:bg-slate-50 ${
                        selectedMarker?.id === marker.id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => setSelectedMarker(marker)}
                      data-testid={`isir-row-${marker.number}`}
                    >
                      <TableCell className="font-mono font-bold">{marker.number}</TableCell>
                      <TableCell>{marker.description || '-'}</TableCell>
                      <TableCell className="text-right font-mono">{marker.nominal ?? '-'}</TableCell>
                      <TableCell className="text-right font-mono">{marker.tol_plus ?? '-'}</TableCell>
                      <TableCell className="text-right font-mono">{marker.tol_minus ?? '-'}</TableCell>
                      <TableCell className="text-right font-mono">{marker.measured ?? '-'}</TableCell>
                      <TableCell>{marker.instrument || '-'}</TableCell>
                      <TableCell>{marker.resolution || '-'}</TableCell>
                      <TableCell className="text-center">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          marker.is_ok === true 
                            ? 'bg-green-100 text-green-700' 
                            : marker.is_ok === false 
                              ? 'bg-red-100 text-red-700'
                              : 'bg-slate-100 text-slate-500'
                        }`}>
                          {marker.is_ok === true ? 'OK' : marker.is_ok === false ? 'NOT OK' : 'N/A'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {markers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-slate-400 py-8">
                        Nessun marker. Clicca su "Aggiungi Marker" e poi sul disegno.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </div>
      </div>

      {/* Instructions Dialog */}
      <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="w-5 h-5 text-primary" />
              {t('how_to_use_smartballoon')}
            </DialogTitle>
            <DialogDescription>
              {t('quick_guide_intro')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">1</div>
              <div>
                <h4 className="font-semibold text-sm">{t('marker_ai')}</h4>
                <p className="text-xs text-slate-600">
                  {t('step_marker_ai')}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50">
              <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-sm">+</div>
              <div>
                <h4 className="font-semibold text-sm">{t('manual_marker')}</h4>
                <p className="text-xs text-slate-600">
                  {t('manual_marker_desc')}
                </p>
                <p className="text-xs text-amber-700 mt-1 font-medium">
                  ⚠️ {t('manual_marker_warning')}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">2</div>
              <div>
                <h4 className="font-semibold text-sm">{t('select_move')}</h4>
                <p className="text-xs text-slate-600">
                  {t('step_select_move')}
                  <br/><span className="text-amber-600">{t('step_select_move_tip')}</span>
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 rounded-lg bg-purple-50">
              <div className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold text-sm">3</div>
              <div>
                <h4 className="font-semibold text-sm">{t('step_detect_ai_title')}</h4>
                <p className="text-xs text-slate-600">
                  {t('step_detect_ai_desc')}
                </p>
                <ul className="text-xs text-slate-600 mt-1 list-disc list-inside space-y-0.5">
                  <li><strong>{t('step_detect_ai_vector')}</strong></li>
                  <li><strong>{t('step_detect_ai_image')}</strong></li>
                  <li>{t('step_detect_ai_tolerances')}</li>
                  <li>{t('step_detect_ai_correct')}</li>
                </ul>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
              <div className="w-8 h-8 rounded-full bg-slate-500 text-white flex items-center justify-center font-bold text-sm">4</div>
              <div>
                <h4 className="font-semibold text-sm">{t('step_define_view_title')}</h4>
                <p className="text-xs text-slate-600">
                  {t('step_define_view_desc')}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm">
                <Download className="w-4 h-4" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">{t('step_export_title')}</h4>
                <p className="text-xs text-slate-600">
                  <span className="font-medium">{t('step_export_pdf')}</span><br/>
                  <span className="font-medium">{t('step_export_excel')}</span>
                </p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowInstructions(false)}>
              {t('got_it_lets_start')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit AI Suggestion Dialog - DRAGGABLE */}
      <Dialog open={!!editingSuggestion} onOpenChange={(open) => {
        if (!open) {
          setEditingSuggestion(null);
          setEditDialogPos({ x: 150, y: 0 });  // Reset to default offset position
        }
      }}>
        <DialogContent 
          className="max-w-md cursor-default max-h-[85vh] overflow-y-auto"
          style={{
            transform: `translate(calc(-50% + ${editDialogPos.x}px), calc(-50% + ${editDialogPos.y}px))`,
            transition: isDraggingDialog ? 'none' : 'transform 0.1s ease-out'
          }}
        >
          {/* Draggable Header */}
          <div
            className="absolute top-0 left-0 right-0 h-14 cursor-move flex items-center justify-center text-xs text-slate-400 select-none"
            onMouseDown={(e) => {
              e.preventDefault();
              setIsDraggingDialog(true);
              dialogDragStart.current = {
                x: e.clientX,
                y: e.clientY,
                startX: editDialogPos.x,
                startY: editDialogPos.y
              };
              
              const handleMove = (moveEvent) => {
                const dx = moveEvent.clientX - dialogDragStart.current.x;
                const dy = moveEvent.clientY - dialogDragStart.current.y;
                setEditDialogPos({
                  x: dialogDragStart.current.startX + dx,
                  y: dialogDragStart.current.startY + dy
                });
              };
              
              const handleUp = () => {
                setIsDraggingDialog(false);
                window.removeEventListener('mousemove', handleMove);
                window.removeEventListener('mouseup', handleUp);
              };
              
              window.addEventListener('mousemove', handleMove);
              window.addEventListener('mouseup', handleUp);
            }}
          >
            <span className="bg-slate-200 rounded-full px-2 py-0.5 text-[10px] font-medium">
              ⋮⋮ Trascina per spostare
            </span>
          </div>
          
          <DialogHeader className="pt-4">
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-500" />
              Modifica Quota
            </DialogTitle>
            <DialogDescription>
              Correggi i valori rilevati dall'AI
            </DialogDescription>
          </DialogHeader>
          
          {editingSuggestion && (
            <EditSuggestionForm 
              suggestion={editingSuggestion}
              markers={markers}
              onSave={(updated) => {
                setAiSuggestions(aiSuggestions.map(s => 
                  s.id === updated.id ? updated : s
                ));
                // Also update selectedSuggestion if it's the same one
                if (selectedSuggestion?.id === updated.id) {
                  setSelectedSuggestion(updated);
                }
                setEditingSuggestion(null);
                setEditDialogPos({ x: 150, y: 0 });
                toast.success('Quota modificata');
              }}
              onAccept={(updated) => {
                acceptAISuggestion(updated);
                setEditingSuggestion(null);
                setEditDialogPos({ x: 150, y: 0 });
                setSelectedSuggestion(null); // Clear selection since it's now a marker
              }}
              onCancel={() => {
                setEditingSuggestion(null);
                setEditDialogPos({ x: 150, y: 0 });
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Marker Dialog */}
      <Dialog open={showManualMarkerDialog} onOpenChange={(open) => {
        if (!open) {
          setShowManualMarkerDialog(false);
          setPendingManualPosition(null);
        }
      }}>
        <DialogContent 
          className="max-w-md max-h-[85vh] overflow-y-auto cursor-default"
          style={{
            transform: `translate(calc(-50% + ${manualDialogPos.x}px), calc(-50% + ${manualDialogPos.y}px))`,
            transition: isDraggingManualDialog ? 'none' : 'transform 0.1s ease-out'
          }}
        >
          <DialogHeader 
            className="cursor-move select-none"
            onMouseDown={(e) => {
              e.preventDefault();
              setIsDraggingManualDialog(true);
              const startX = e.clientX - manualDialogPos.x;
              const startY = e.clientY - manualDialogPos.y;
              
              const handleMouseMove = (moveEvent) => {
                setManualDialogPos({
                  x: moveEvent.clientX - startX,
                  y: moveEvent.clientY - startY
                });
              };
              
              const handleMouseUp = () => {
                setIsDraggingManualDialog(false);
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
              };
              
              document.addEventListener('mousemove', handleMouseMove);
              document.addEventListener('mouseup', handleMouseUp);
            }}
          >
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-amber-500" />
              {t('manual_marker_insert')}
              <span className="text-xs text-slate-400 font-normal ml-2">(trascina per spostare)</span>
            </DialogTitle>
            <DialogDescription>
              {t('manual_marker_desc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Descrizione Quota */}
            <div>
              <Label className="text-sm font-medium">Descrizione Quota</Label>
              <Input
                type="text"
                value={manualMarkerData.description || ''}
                onChange={(e) => setManualMarkerData({
                  ...manualMarkerData,
                  description: e.target.value
                })}
                className="mt-1"
                placeholder="es. Ø47 +0,1/0, Larghezza flangia"
              />
            </div>
            
            {/* Valore Nominale */}
            <div>
              <Label className="text-sm font-medium">Valore Nominale *</Label>
              <Input
                type="text"
                value={manualMarkerData.nominal}
                onChange={(e) => setManualMarkerData({
                  ...manualMarkerData,
                  nominal: e.target.value
                })}
                className="mt-1"
                placeholder="es. 21.5, 16.6, 10"
                autoFocus
              />
            </div>
            
            {/* Tolleranze */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Tolleranza Superiore</Label>
                <Input
                  type="text"
                  value={manualMarkerData.tol_plus}
                  onChange={(e) => setManualMarkerData({
                    ...manualMarkerData,
                    tol_plus: e.target.value
                  })}
                  className="mt-1"
                  placeholder="es. +0.1 o 0.1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Tolleranza Inferiore</Label>
                <Input
                  type="text"
                  value={manualMarkerData.tol_minus}
                  onChange={(e) => setManualMarkerData({
                    ...manualMarkerData,
                    tol_minus: e.target.value
                  })}
                  className="mt-1"
                  placeholder="es. -0.1 o 0.1"
                />
              </div>
            </div>
            
            {/* Strumento e Risoluzione con autocomplete */}
            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <Label className="text-sm font-medium">Strumento di Misura</Label>
                <Input
                  type="text"
                  value={manualMarkerData.instrument || ''}
                  onChange={(e) => {
                    setManualMarkerData({
                      ...manualMarkerData,
                      instrument: e.target.value
                    });
                  }}
                  className="mt-1"
                  placeholder="es. calibro, micrometro"
                />
                {/* Autocomplete suggestions */}
                {manualMarkerData.instrument && manualMarkerData.instrument.length >= 2 && (
                  (() => {
                    const search = manualMarkerData.instrument.toLowerCase();
                    const suggestions = Array.from(new Map(
                      markers.filter(m => m.instrument && m.instrument.toLowerCase().includes(search))
                        .map(m => [m.instrument.toLowerCase(), { instrument: m.instrument, resolution: m.resolution }])
                    ).values()).slice(0, 5);
                    
                    if (suggestions.length === 0) return null;
                    
                    return (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-32 overflow-auto">
                        {suggestions.map((item, idx) => (
                          <div
                            key={idx}
                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer flex justify-between items-center text-sm"
                            onClick={() => {
                              setManualMarkerData({
                                ...manualMarkerData,
                                instrument: item.instrument,
                                resolution: item.resolution || manualMarkerData.resolution
                              });
                            }}
                          >
                            <span className="font-medium">{item.instrument}</span>
                            {item.resolution && (
                              <span className="text-slate-400 text-xs">ris: {item.resolution}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()
                )}
              </div>
              <div>
                <Label className="text-sm font-medium">Risoluzione</Label>
                <Input
                  type="text"
                  value={manualMarkerData.resolution || ''}
                  onChange={(e) => setManualMarkerData({
                    ...manualMarkerData,
                    resolution: e.target.value
                  })}
                  className="mt-1"
                  placeholder="es. 0.01, 0.001"
                />
              </div>
            </div>
            
            <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
              <strong>Suggerimento:</strong> Per tolleranze simmetriche (es. ±0.1), inserisci 0.1 in entrambi i campi.
              Digita nel campo strumento per vedere gli strumenti già usati.
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowManualMarkerDialog(false);
              setPendingManualPosition(null);
              setManualDialogPos({ x: 0, y: 0 });
            }}>
              Annulla
            </Button>
            <Button 
              onClick={() => {
                // Parse nominal value
                let nominal = parseFloat(manualMarkerData.nominal.replace(',', '.').replace(/[^\d.-]/g, ''));
                if (isNaN(nominal)) {
                  toast.error('Inserisci un valore nominale valido');
                  return;
                }
                
                // Parse tolerances
                let tol_plus = manualMarkerData.tol_plus ? 
                  parseFloat(manualMarkerData.tol_plus.replace(',', '.').replace(/[^\d.-]/g, '')) : null;
                let tol_minus = manualMarkerData.tol_minus ? 
                  parseFloat(manualMarkerData.tol_minus.replace(',', '.').replace(/[^\d.-]/g, '')) : null;
                
                // Save current state for undo
                setUndoStack(prev => [...prev, [...markers]]);
                
                // Create marker
                const newMarker = {
                  id: uuidv4(),
                  number: markers.length + 1,
                  x: pendingManualPosition.x,
                  y: pendingManualPosition.y,
                  description: manualMarkerData.description || manualMarkerData.nominal,
                  nominal: nominal,
                  tol_plus: tol_plus,
                  tol_minus: tol_minus,
                  measured: null,
                  instrument: manualMarkerData.instrument || '',
                  resolution: manualMarkerData.resolution || '',
                  is_ok: null,
                  is_ai_detected: false
                };
                
                const newMarkers = [...markers, newMarker];
                setMarkers(newMarkers);
                setSelectedMarker(newMarker);
                saveMarkers(newMarkers);
                
                // Switch to select mode after adding
                setTool('select');
                
                toast.success(`Marker aggiunto: ${manualMarkerData.description || nominal}`);
                setShowManualMarkerDialog(false);
                setPendingManualPosition(null);
                setManualDialogPos({ x: 0, y: 0 });
                setManualMarkerData({ nominal: '', tol_plus: '', tol_minus: '', description: '', instrument: '', resolution: '' });
              }}
              disabled={!manualMarkerData.nominal}
            >
              Aggiungi Marker
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CMM Report Preview Dialog */}
      <Dialog open={showCMMPreview} onOpenChange={setShowCMMPreview}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Anteprima Importazione CMM
              {cmmStats?.samples_count > 1 && (
                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                  {cmmStats.samples_count} campioni
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              {cmmStats?.samples_count > 1 
                ? 'Ogni report CMM rappresenta un campione/pezzo diverso. Seleziona i marker da importare.'
                : 'Seleziona i valori da importare nei marker. Solo i marker esistenti possono essere aggiornati.'
              }
            </DialogDescription>
          </DialogHeader>
          
          {cmmStats && (
            <div className="grid grid-cols-4 gap-2 p-3 bg-slate-50 rounded-lg text-sm">
              <div className="text-center">
                <p className="font-bold text-lg text-slate-700">{cmmStats.total_sb_ids || cmmStats.total_found}</p>
                <p className="text-xs text-slate-500">Marker SB trovati</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-lg text-green-600">{cmmStats.matched_markers}</p>
                <p className="text-xs text-slate-500">Marker corrispondenti</p>
              </div>
              {cmmStats.samples_count > 1 ? (
                <div className="text-center">
                  <p className="font-bold text-lg text-blue-600">{cmmStats.samples_count}</p>
                  <p className="text-xs text-slate-500">Campioni (P1, P2...)</p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="font-bold text-lg text-amber-600">{cmmStats.unmatched || 0}</p>
                  <p className="text-xs text-slate-500">Non trovati</p>
                </div>
              )}
              <div className="text-center">
                <p className="font-bold text-lg text-slate-600">{cmmStats.reports_parsed}</p>
                <p className="text-xs text-slate-500">Report analizzati</p>
              </div>
            </div>
          )}
          
          <div className="flex-1 overflow-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="p-2 text-left w-10">
                    <input
                      type="checkbox"
                      checked={selectedCMMRows.size === cmmMeasurements.filter(m => m.marker_exists).length && selectedCMMRows.size > 0}
                      onChange={toggleAllCMMRows}
                      className="rounded"
                    />
                  </th>
                  <th className="p-2 text-left">SB ID</th>
                  {cmmStats?.samples_count > 1 ? (
                    // Multi-sample headers - use custom names if available
                    <>
                      {Array.from({length: cmmStats.samples_count}, (_, i) => (
                        <th key={i} className="p-2 text-center text-blue-700">
                          {cmmStats.sample_names?.[i] || `P${i + 1}`}
                        </th>
                      ))}
                    </>
                  ) : (
                    // Single-sample headers
                    <>
                      <th className="p-2 text-left">Valore Estratto</th>
                      <th className="p-2 text-left">Valore Attuale</th>
                    </>
                  )}
                  <th className="p-2 text-left">Stato</th>
                </tr>
              </thead>
              <tbody>
                {cmmMeasurements.map((m) => (
                  <tr 
                    key={m.sb_id} 
                    className={`border-t ${
                      !m.marker_exists ? 'bg-red-50 opacity-60' : 
                      selectedCMMRows.has(m.sb_id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={selectedCMMRows.has(m.sb_id)}
                        onChange={() => toggleCMMRow(m.sb_id)}
                        disabled={!m.marker_exists}
                        className="rounded"
                      />
                    </td>
                    <td className="p-2 font-mono font-bold">SB{m.sb_id}</td>
                    {m.sample_values ? (
                      // Multi-sample values
                      <>
                        {Array.from({length: cmmStats?.samples_count || 1}, (_, i) => {
                          const sv = m.sample_values.find(s => s.sample === i + 1);
                          const value = sv?.value;
                          // Check if out of tolerance
                          let isOutOfTol = false;
                          if (value !== undefined && m.nominal !== undefined && m.nominal !== null) {
                            const upper = m.nominal + (m.tol_plus || 0);
                            const lower = m.nominal - Math.abs(m.tol_minus || 0);
                            isOutOfTol = value < lower || value > upper;
                          }
                          return (
                            <td key={i} className={`p-2 text-center font-mono ${isOutOfTol ? 'text-red-600 font-bold' : 'text-blue-700'}`}>
                              {value !== undefined ? value : '-'}
                            </td>
                          );
                        })}
                      </>
                    ) : (
                      // Single-sample values
                      <>
                        <td className="p-2 font-mono text-blue-700">{m.measured_value}</td>
                        <td className="p-2 font-mono text-slate-500">
                          {m.current_value !== null ? m.current_value : '-'}
                        </td>
                      </>
                    )}
                    <td className="p-2">
                      {!m.marker_exists ? (
                        <span className="text-red-600 text-xs">Marker non esiste</span>
                      ) : m.current_value !== null || m.current_measurements ? (
                        <span className="text-amber-600 text-xs">Sovrascriverà</span>
                      ) : (
                        <span className="text-green-600 text-xs">OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCMMPreview(false)}
            >
              Annulla
            </Button>
            <Button
              onClick={() => {
                // Use multi-sample apply if we have sample_values
                if (cmmMeasurements.some(m => m.sample_values)) {
                  handleApplyCMMMeasurementsMultiSample();
                } else {
                  handleApplyCMMMeasurements();
                }
              }}
              disabled={applyingCMM || selectedCMMRows.size === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {applyingCMM ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Importa {selectedCMMRows.size} misure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Tolerance Class Selection Modal */}
      <Dialog open={showToleranceModal} onOpenChange={setShowToleranceModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-500" />
              Seleziona Classe di Tolleranza
            </DialogTitle>
            <DialogDescription>
              La tolleranza generale non è stata trovata nel cartiglio. Seleziona la classe ISO 2768 da applicare alle quote senza tolleranza specifica.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Classe di tolleranza</label>
              <select
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={selectedToleranceClass}
                onChange={(e) => setSelectedToleranceClass(e.target.value)}
              >
                <option value="f">ISO 2768-fK (Fine)</option>
                <option value="m">ISO 2768-mK (Media) - Predefinita</option>
                <option value="c">ISO 2768-cK (Grossolana)</option>
                <option value="v">ISO 2768-vK (Molto Grossolana)</option>
                <option value="manual">Inserimento Manuale</option>
              </select>
            </div>
            
            {selectedToleranceClass === 'manual' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Tolleranza (±mm)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={manualTolerance}
                  onChange={(e) => setManualTolerance(e.target.value)}
                  placeholder="es. 0.1"
                />
                <p className="text-xs text-slate-500">Questa tolleranza verrà applicata a tutte le quote senza tolleranza specifica</p>
              </div>
            )}
            
            <div className="bg-slate-50 rounded-lg p-3 text-xs">
              <p className="font-medium mb-1">Riferimento ISO 2768 (quota 30mm):</p>
              <ul className="space-y-0.5 text-slate-600">
                <li>• Fine (f): ±0.1 mm</li>
                <li>• Media (m): ±0.2 mm</li>
                <li>• Grossolana (c): ±0.3 mm</li>
                <li>• Molto Grossolana (v): ±0.5 mm</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowToleranceModal(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveToleranceClass}>
              Applica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Marker Number Change Confirmation Dialog */}
      <Dialog open={!!pendingNumberChange} onOpenChange={(open) => {
        if (!open) cancelMarkerNumberChange();
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Info className="w-5 h-5" />
              Numero Marker Esistente
            </DialogTitle>
            <DialogDescription>
              Il marker numero <strong className="text-slate-900">{pendingNumberChange?.newNumber}</strong> esiste già.
              <br /><br />
              Se confermi, il marker corrente diventerà <strong>#{pendingNumberChange?.newNumber}</strong> e tutti gli altri marker con numero uguale o superiore verranno rinumerati (scalati di +1).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={cancelMarkerNumberChange}>
              Annulla
            </Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={confirmMarkerNumberChange}
            >
              Sì, Rinumera
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* AI Suggestion Accept with Renumber Confirmation Dialog */}
      <Dialog open={!!pendingAISuggestionAccept} onOpenChange={(open) => {
        if (!open) setPendingAISuggestionAccept(null);
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Info className="w-5 h-5" />
              Numero Marker Esistente
            </DialogTitle>
            <DialogDescription>
              Il marker numero <strong className="text-slate-900">{pendingAISuggestionAccept?.newNumber}</strong> esiste già tra i marker confermati.
              <br /><br />
              Se confermi, il suggerimento AI diventerà <strong>#{pendingAISuggestionAccept?.newNumber}</strong> e tutti gli altri marker con numero uguale o superiore verranno rinumerati (scalati di +1).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPendingAISuggestionAccept(null)}>
              Annulla
            </Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={confirmAISuggestionWithRenumber}
            >
              Sì, Rinumera
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Rescan Confirmation Dialog */}
      <Dialog open={showRescanConfirm} onOpenChange={setShowRescanConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Info className="w-5 h-5" />
              Attenzione: Quote già presenti
            </DialogTitle>
            <DialogDescription className="space-y-3">
              <p>
                {markers.length > 0 && aiSuggestions.length > 0 ? (
                  <>Hai già <strong>{markers.length} marker confermati</strong> e <strong>{aiSuggestions.length} suggerimenti AI</strong> in attesa.</>
                ) : markers.length > 0 ? (
                  <>Hai già <strong>{markers.length} marker confermati</strong> su questo disegno.</>
                ) : (
                  <>Hai già <strong>{aiSuggestions.length} quote rilevate</strong> in attesa di conferma.</>
                )}
              </p>
              <p>
                Se rilanci la scansione completa, le quote verranno rilette e <strong>rischi di creare duplicati</strong>!
              </p>
              <p className="text-sm bg-blue-50 p-2 rounded border border-blue-200">
                <strong>Suggerimento:</strong> Se ti serve analizzare solo una zona specifica, usa invece la funzione <strong>"Definisci Vista"</strong> o <strong>"Marker AI"</strong> (click singolo).
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowRescanConfirm(false)}>
              Annulla
            </Button>
            <Button 
              variant="destructive"
              onClick={() => {
                setShowRescanConfirm(false);
                // Proceed with the scan
                if (vectorInfo?.is_vector_pdf) {
                  handleDetectVector();
                } else {
                  handleDetectAllTiled();
                }
              }}
            >
              Sì, Rilancia Scansione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* CMM Import Mode Selection Dialog */}
      <Dialog open={showCMMImportModeDialog} onOpenChange={setShowCMMImportModeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <FileText className="w-5 h-5" />
              {t('cmm_interpret_title')}
            </DialogTitle>
            <DialogDescription dangerouslySetInnerHTML={{ __html: t('cmm_interpret_desc', { count: cmmReports.length }) }} />
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            {/* Option 1: Manual assignment */}
            <label 
              className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                cmmImportMode === 'manual_assign' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-slate-200 hover:border-blue-300'
              }`}
            >
              <input
                type="radio"
                name="cmm-import-mode"
                value="manual_assign"
                checked={cmmImportMode === 'manual_assign'}
                onChange={() => setCmmImportMode('manual_assign')}
                className="mt-1 w-4 h-4 text-blue-600"
              />
              <div>
                <div className="font-semibold text-slate-800">{t('cmm_mode_manual_title')}</div>
                <div className="text-sm text-slate-600 mt-1" dangerouslySetInnerHTML={{ __html: t('cmm_mode_manual_desc') }} />
                <span className="text-xs text-slate-500 block mt-1">{t('cmm_mode_manual_hint')}</span>
              </div>
            </label>
            
            {/* Option 2: Auto multi-sample */}
            <label 
              className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                cmmImportMode === 'multi_sample' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-slate-200 hover:border-blue-300'
              }`}
            >
              <input
                type="radio"
                name="cmm-import-mode"
                value="multi_sample"
                checked={cmmImportMode === 'multi_sample'}
                onChange={() => setCmmImportMode('multi_sample')}
                className="mt-1 w-4 h-4 text-blue-600"
              />
              <div>
                <div className="font-semibold text-slate-800">{t('cmm_mode_multi_title')}</div>
                <div className="text-sm text-slate-600 mt-1" dangerouslySetInnerHTML={{ __html: t('cmm_mode_multi_desc') }} />
                <span className="text-xs text-slate-500 block mt-1">{t('cmm_mode_multi_hint')}</span>
              </div>
            </label>
            
            {/* Option 3: Duplicate resolution */}
            <label 
              className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                cmmImportMode === 'duplicate' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-slate-200 hover:border-blue-300'
              }`}
            >
              <input
                type="radio"
                name="cmm-import-mode"
                value="duplicate"
                checked={cmmImportMode === 'duplicate'}
                onChange={() => setCmmImportMode('duplicate')}
                className="mt-1 w-4 h-4 text-blue-600"
              />
              <div>
                <div className="font-semibold text-slate-800">{t('cmm_mode_duplicate_title')}</div>
                <div className="text-sm text-slate-600 mt-1" dangerouslySetInnerHTML={{ __html: t('cmm_mode_duplicate_desc') }} />
                <span className="text-xs text-slate-500 block mt-1">{t('cmm_mode_duplicate_hint')}</span>
              </div>
            </label>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCMMImportModeDialog(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={() => {
              console.log('[CMM] Mode selected:', cmmImportMode, 'Reports:', cmmReports.length);
              if (cmmImportMode === 'manual_assign') {
                // Open manual assignment dialog
                const assignments = cmmReports.map((r, idx) => ({
                  report_id: r.id,
                  filename: r.filename,
                  sample_name: `P${idx + 1}`
                }));
                console.log('[CMM] Opening manual assignment dialog with:', assignments);
                setCmmReportAssignments(assignments);
                setShowCMMImportModeDialog(false);
                // Use setTimeout to ensure the first dialog closes before opening the second
                setTimeout(() => {
                  setShowCMMManualAssignDialog(true);
                }, 100);
              } else {
                executeCMMParsing(cmmImportMode);
              }
            }}>
              Continua
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* CMM Manual Assignment Dialog */}
      <Dialog open={showCMMManualAssignDialog} onOpenChange={setShowCMMManualAssignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <FileText className="w-5 h-5" />
              {t('cmm_assign_title')}
            </DialogTitle>
            <DialogDescription>
              {t('cmm_assign_desc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3 max-h-[400px] overflow-y-auto">
            {cmmReportAssignments.map((assignment, idx) => (
              <div key={assignment.report_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate" title={assignment.filename}>
                    {assignment.filename}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">→</span>
                  <Input
                    value={assignment.sample_name}
                    onChange={(e) => {
                      const updated = [...cmmReportAssignments];
                      updated[idx].sample_name = e.target.value;
                      setCmmReportAssignments(updated);
                    }}
                    placeholder={t('cmm_assign_placeholder')}
                    className="w-32 text-center font-medium"
                  />
                </div>
              </div>
            ))}
          </div>
          
          <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
            <strong>{t('guide_suggestion')}</strong> {t('cmm_assign_tip')}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCMMManualAssignDialog(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={() => executeCMMParsingWithAssignments()}
              disabled={cmmReportAssignments.some(a => !a.sample_name.trim())}
            >
              {t('cmm_continue_import')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* CMM Duplicate Resolution Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Info className="w-5 h-5" />
              {t('cmm_duplicate_title', { id: cmmDuplicates[currentDuplicateIndex]?.sb_id })}
              {cmmDuplicates[currentDuplicateIndex]?.sample_name && (
                <span className="text-sm font-normal text-slate-500">
                  ({cmmDuplicates[currentDuplicateIndex].sample_name})
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              <span className="text-slate-600">
                {t('cmm_duplicate_desc')}
              </span>
              <span className="block mt-1 text-sm text-slate-500">
                {t('cmm_duplicate_count', { current: currentDuplicateIndex + 1, total: cmmDuplicates.length })}
              </span>
            </DialogDescription>
          </DialogHeader>
          
          {cmmDuplicates[currentDuplicateIndex] && (
            <div className="py-4 space-y-3">
              {/* Marker info if exists */}
              {cmmDuplicates[currentDuplicateIndex].marker_exists && (
                <div className="p-2 bg-green-50 border border-green-200 rounded text-sm">
                  <span className="font-medium text-green-700">{t('cmm_duplicate_existing')}</span>
                  <span className="ml-2 text-green-600">
                    {cmmDuplicates[currentDuplicateIndex].marker_description || `SB${cmmDuplicates[currentDuplicateIndex].sb_id}`}
                  </span>
                  {cmmDuplicates[currentDuplicateIndex].current_value && (
                    <span className="ml-2 text-green-600">
                      {t('cmm_duplicate_current', { value: cmmDuplicates[currentDuplicateIndex].current_value })}
                    </span>
                  )}
                </div>
              )}
              
              {/* Radio options for each occurrence */}
              <div className="space-y-2">
                {cmmDuplicates[currentDuplicateIndex].occurrences.map((occ, idx) => (
                  <label 
                    key={idx}
                    className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition-colors ${
                      resolvedDuplicates[cmmDuplicates[currentDuplicateIndex].sb_id]?.report_id === occ.report_id &&
                      resolvedDuplicates[cmmDuplicates[currentDuplicateIndex].sb_id]?.measured_value === occ.measured_value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`duplicate-${cmmDuplicates[currentDuplicateIndex].sb_id}`}
                      checked={
                        resolvedDuplicates[cmmDuplicates[currentDuplicateIndex].sb_id]?.report_id === occ.report_id &&
                        resolvedDuplicates[cmmDuplicates[currentDuplicateIndex].sb_id]?.measured_value === occ.measured_value
                      }
                      onChange={() => handleSelectDuplicate(cmmDuplicates[currentDuplicateIndex].sb_id, occ)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-lg text-slate-800">
                          {occ.measured_value}
                        </span>
                        <span className="text-xs text-slate-500">mm</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {occ.filename}
                      </div>
                      {occ.raw_text && (
                        <div className="text-xs text-slate-400 mt-1 truncate" title={occ.raw_text}>
                          "{occ.raw_text}"
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter className="flex justify-between gap-2">
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handlePrevDuplicate}
                disabled={currentDuplicateIndex === 0}
              >
                {t('cmm_duplicate_prev')}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDuplicateDialog(false);
                  setCmmDuplicates([]);
                  setResolvedDuplicates({});
                }}
              >
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleNextDuplicate}
                disabled={!resolvedDuplicates[cmmDuplicates[currentDuplicateIndex]?.sb_id]}
              >
                {currentDuplicateIndex < cmmDuplicates.length - 1 ? t('cmm_duplicate_next') : t('cmm_duplicate_confirm_all')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* SmartBalloon Tutorial */}
      <TutorialOverlay
        tutorialId="smartballoon_intro"
        steps={getSmartBalloonTutorialSteps(t)}
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
        onComplete={() => setShowTutorial(false)}
        token={token}
      />
      
      {/* Trial Export Block Modal */}
      <TrialUpgradeModal
        isOpen={trialExportModalOpen}
        onClose={() => setTrialExportModalOpen(false)}
        type="trial_export_blocked"
      />
    </div>
  );
};

export default WorkspacePage;
